import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption, ColumnSelector, useColumnVisibility, type ColumnDefinition } from 'components/ui';
import { EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan } from 'components/enterprise-shell';
import DocumentNumberField from 'components/common/DocumentNumberField';
import notificationService from 'services/notificationService';
import { useNavTarget } from 'contexts/NavigationContext';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { usePurchaseInvoices, usePurchaseLineLedgers } from 'hooks/api/useCommercial';
import { purchaseInvoiceService, partyService, voucherTypeService, taxMetaService, taxProfileService, entityAccountingConfigService, currencyService, coaLedgerService } from 'api/services';
import SetupGateModal, { useSetupGate } from 'components/modules/setup/SetupGateModal';
import {
  useItemLineLookups, blankItemFieldValues, itemFieldValuesOf, itemLabel, ledgerSourceTag, effectiveItemLedgerId, prefillLedgerLabel,
  ItemPickerCell, ItemLineDetails, ItemLineSummary, type ItemFields, type ItemLineLookups,
} from 'components/modules/shared/itemLines';
import type { PurchaseDocumentKind, PurchaseInvoiceDto, PurchaseInvoiceQueryParams, CreatePurchaseInvoiceRequest, CreatePurchaseInvoiceLineRequest, CreatePurchaseInvoiceTaxRequest, PurchaseChargeAllocationDto, PurchaseLineType, ChargeAllocationBasis, SupplierDuplicateProbe, VoucherTypeDto, PartyDto, TaxComponentDto, InputTaxRulesDto, ItcEligibility, PurchaseType, DocumentLineLedgerDto, TaxCalculationResult } from 'api/types';
import { FyDateInput } from 'components/common/FyDateInput';
import { useSelectedFinancialYear, defaultDateIn } from 'hooks/useSelectedFinancialYear';

// ADR-0049 §3/§4a: an EXPENSE line is LEDGER + AMOUNT (qty/rate optional arithmetic).
// ADR-0058 slice 3: an ITEM line buys an inventory item (item × pack × qty, location for stock
// items); an EXPENSE charge can be added to the items' stock cost. Supplier debit notes stay EXPENSE-only.
const PURCHASE_TYPE_OPTIONS: SelectOption[] = [
  { value: 'DOMESTIC', label: 'Domestic' },
  { value: 'IMPORT_GOODS', label: 'Import — Goods' },
  { value: 'IMPORT_SERVICES', label: 'Import — Services' },
];
const ELIGIBILITY_OPTIONS: SelectOption[] = [
  { value: 'ELIGIBLE', label: 'Eligible (default — can be revised later)' },
  { value: 'INELIGIBLE', label: 'Ineligible — tax is cost' },
  { value: 'PARTIAL', label: 'Partially eligible' },
];
const today = () => new Date().toISOString().slice(0, 10);
const money = (n: number) => (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const addDays = (dateStr: string, days: number): string => {
  if (!dateStr) return dateStr;
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + (days || 0));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
};
const formatAddress = (p: PartyDto): string =>
  [p.addressLine1, p.city, p.stateName, p.postalCode, p.countryName].filter(Boolean).join(', ');

/**
 * Why a FULL edit (amounts, ledgers, tax) is not possible — null when it is. Needs the full
 * record (otherSettlements). Linked debit notes do NOT block: the server releases them,
 * re-posts the bill and re-applies them (§7, Slice 2c). Edit details stays available.
 */
const editBlockReason = (r: PurchaseInvoiceDto): string | null => {
  if (r.docStatus !== 'POSTED') return r.docStatus === 'CANCELLED' ? 'Cancelled' : 'Not posted';
  if (r.isLocked) return r.lockSource || 'Locked (filed or closed period)';
  if ((r.otherSettlements?.length ?? 0) > 0)
    return `Settled by ${r.otherSettlements!.map(s => `${s.voucherLabel || 'voucher'} ${s.transactionNo || ''}`.trim()).join(', ')}`;
  if ((r.adjustments?.length ?? 0) > 0) return 'Has ITC adjustments';
  return null;
};
const isBillEditable = (r: PurchaseInvoiceDto): boolean => editBlockReason(r) === null;
const eligibilityBadge = (e: ItcEligibility): React.ReactNode =>
  e === 'ELIGIBLE' ? null
  : <span className="badge" style={{ background: e === 'INELIGIBLE' ? '#6b7280' : '#d97706', color: '#fff' }}>{e === 'INELIGIBLE' ? 'ITC Ineligible' : 'ITC Partial'}</span>;
const statusBadges = (r: PurchaseInvoiceDto): React.ReactNode => (
  <span style={{ display: 'inline-flex', gap: '0.375rem', alignItems: 'center', flexWrap: 'wrap' }}>
    <span className="badge">{r.docStatus}</span>
    {r.settlementStatus === 'PARTIAL' && <span className="badge" style={{ background: '#d97706', color: '#fff' }}>Partially Paid</span>}
    {r.settlementStatus === 'SETTLED' && <span className="badge" style={{ background: '#16a34a', color: '#fff' }}>Fully Paid</span>}
    {eligibilityBadge(r.itcEligibility)}
    {r.isLocked && <span className="badge" style={{ background: '#6b7280', color: '#fff' }} title={r.lockSource || 'Locked'}>🔒 Locked</span>}
  </span>
);

// ---- Item lines (ADR-0058 slice 3) — shared with the Purchase Debit Note screen ----
// The item half, its lookups and its views live in shared/itemLines (sales uses them too);
// re-exported here so the purchase screens keep importing them from this file.
export { itemLabel, ledgerSourceTag, effectiveItemLedgerId, prefillLedgerLabel, ItemPickerCell, ItemLineDetails, ItemLineSummary };
export const LINE_TYPE_OPTIONS: SelectOption[] = [
  { value: 'EXPENSE', label: 'Expense' },
  { value: 'ITEM', label: 'Item' },
];
const ALLOCATION_BASIS_OPTIONS: SelectOption[] = [
  { value: 'VALUE', label: 'By value' },
  { value: 'QUANTITY', label: 'By quantity' },
  { value: 'WEIGHT', label: 'By weight' },
  { value: 'MANUAL', label: 'Manual' },
];
const BASIS_LABEL: Record<string, string> = { VALUE: 'by value', QUANTITY: 'by quantity', WEIGHT: 'by weight', MANUAL: 'manual shares' };

/** The item half of a purchase entry row: the shared item fields plus the purchase line type. */
export interface ItemLineFields extends ItemFields {
  lineType: PurchaseLineType;
}
export const blankItemFields = (): ItemLineFields => ({ lineType: 'EXPENSE', ...blankItemFieldValues() });
/** A saved (or prefilled) line's item half. */
export const itemFieldsOf = (l: Parameters<typeof itemFieldValuesOf>[0] & { lineType?: string | null }): ItemLineFields => {
  if (l.lineType !== 'ITEM' && !l.itemId) return blankItemFields();
  return { lineType: 'ITEM', ...itemFieldValuesOf(l) };
};

/**
 * Item / pack / location lookups for purchase entry lines. Items blocked for purchase are left
 * out; locations are storage locations only (never in-transit). Where each item line posts comes
 * from the document's line-ledgers read (usePurchaseLineLedgers), not from here.
 */
export const usePurchaseItemLookups = () => useItemLineLookups('PURCHASE');
export type PurchaseItemLookups = ItemLineLookups;

/**
 * MANUAL shares of a saved charge, as editable amounts in the charge's own currency. The stored
 * allocations include any non-recoverable tax share of the charge, so the split is rescaled to
 * the line amount (the last share absorbs the cents).
 */
const rehydrateManualShares = (amount: number, allocs: PurchaseChargeAllocationDto[], keyOfLineNo: Map<number, number>): Record<number, string> => {
  const out: Record<number, string> = {};
  const total = allocs.reduce((s, a) => s + a.amount, 0);
  if (!allocs.length || total === 0) return out;
  let given = 0;
  allocs.forEach((a, i) => {
    const share = i === allocs.length - 1
      ? Math.round((amount - given) * 100) / 100
      : Math.round((amount * a.amount / total) * 100) / 100;
    given += share;
    const key = keyOfLineNo.get(a.itemLineNo);
    if (key != null) out[key] = share.toFixed(2);
  });
  return out;
};

/** Per-line captured tax (LINE mode, §4a addendum): componentCode → amounts as typed. */
type LineTaxes = Record<string, { taxAmount: string; eligibleAmount: string }>;
interface LineRow extends ItemLineFields {
  key: number; description: string; expenseLedgerId: string; quantity: string; rate: string; lineAmount: string; taxes: LineTaxes;
  /** EXPENSE lines on a bill with item lines: the charge debits the items' stock cost (ADR-0058 slice 3). */
  addToStockCost: boolean;
  allocationBasis: ChargeAllocationBasis;
  /** MANUAL basis: ITEM row key → amount as typed. */
  manualShares: Record<number, string>;
  /**
   * ITEM lines: the user typed this line's tax — recalculation (tax preview) leaves it alone until
   * "Recalculate". Lines read from a saved bill start edited: their tax is what was printed.
   */
  taxEdited: boolean;
}
const blankLine = (key: number): LineRow => ({ key, description: '', expenseLedgerId: '', quantity: '', rate: '', lineAmount: '', taxes: {}, ...blankItemFields(), addToStockCost: false, allocationBasis: 'VALUE', manualShares: {}, taxEdited: false });
/** The engine's figures for one item line (tax preview), by the pack's component code. */
type ComputedLineTax = Record<string, { taxAmount: number; taxableBase: number; ratePercentage: number }>;
/** The last tax preview, mapped onto the form's rows. */
interface TaxPreviewState {
  result: TaxCalculationResult;
  /** Row key → computed components (only lines the engine answered for). */
  byKey: Record<number, ComputedLineTax>;
  /** Row key → errors naming that line ("Line N"). */
  lineErrors: Record<number, string[]>;
  /** Errors not tied to a line. */
  errors: string[];
}
const rateText = (r: number) => `${Number(r.toFixed(4))}%`;
/** Line taxes as the engine computed them — typed eligible amounts carry over. */
const computedTaxes = (computed: ComputedLineTax, prev: LineTaxes): LineTaxes =>
  Object.fromEntries(Object.entries(computed)
    .filter(([, c]) => c.taxAmount !== 0)
    .map(([code, c]) => [code, { taxAmount: c.taxAmount.toFixed(2), eligibleAmount: prev[code]?.eligibleAmount ?? '' }]));
/** True when the line's typed tax differs from the computed figures (any component, > 0.005). */
const differsFromComputed = (taxes: LineTaxes, computed: ComputedLineTax): boolean => {
  const codes = new Set([...Object.keys(taxes), ...Object.keys(computed)]);
  return Array.from(codes).some(code => Math.abs((parseFloat(taxes[code]?.taxAmount) || 0) - (computed[code]?.taxAmount || 0)) > 0.005);
};
/** A line that goes to the server: an amount, or an item picked (its errors then surface). */
const isSubmitted = (l: LineRow) => parseFloat(l.lineAmount) > 0 || (l.lineType === 'ITEM' && !!l.itemId);
/** One captured tax component row (INVOICE mode): the amount as printed on the supplier's invoice (§4a). */
interface TaxRow { componentCode: string; componentName: string; taxAmount: string; eligibleAmount: string; }

// ---- Entry modal ----
interface BillFormModalProps {
  isOpen: boolean;
  mode?: 'create' | 'edit' | 'copy';
  invoice?: PurchaseInvoiceDto | null;
  onSaved: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  createInstanceId?: string;
  copyInstanceId?: string;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, invoiceNo?: string, kind?: PurchaseDocumentKind) => void;
  minimizeRef?: React.RefObject<(() => void) | null>;
  /**
   * Create: which document to record (ADR-0053). DEBIT_NOTE records a Supplier Debit Note — the
   * same form, debit-note voucher types, an optional bill it adds to and a reason. Edit takes
   * the kind from the document itself.
   */
  documentKind?: PurchaseDocumentKind;
  /** Create a supplier debit note: the bill it adds to — seeds the supplier, type and currency. */
  addsTo?: PurchaseInvoiceDto | null;
}
export const BillFormModal: React.FC<BillFormModalProps> = ({ isOpen, mode = 'create', invoice, onSaved, onClose, tabId, moduleName, createInstanceId, copyInstanceId, onRestoreModal, minimizeRef, documentKind = 'INVOICE', addsTo }) => {
  const fy = useSelectedFinancialYear();
  const fyRef = useRef(fy); fyRef.current = fy;
  /** Default posting date: today when it falls in the selected financial year, else the year's last (or first) day. */
  const docToday = () => defaultDateIn(fyRef.current);
  const { minimize } = useModalMinimize();
  const isRestoreRef = useRef(false);
  const invoiceRef = useRef(invoice); invoiceRef.current = invoice;
  const addsToRef = useRef(addsTo); addsToRef.current = addsTo;
  const modeRef = useRef(mode); modeRef.current = mode;
  const kind: PurchaseDocumentKind = mode === 'edit' && invoice ? (invoice.documentKind || 'INVOICE') : documentKind;
  const isDebitNote = kind === 'DEBIT_NOTE';
  const kindRef = useRef(kind); kindRef.current = kind;
  const docLabel = isDebitNote ? 'Supplier Debit Note' : 'Bill';
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const copyInstanceIdRef = useRef(copyInstanceId); copyInstanceIdRef.current = copyInstanceId;
  const isCopy = mode === 'copy';
  const [editId, setEditId] = useState<string | null>(null);
  const [editRowVersion, setEditRowVersion] = useState(0);
  const [voucherTypeId, setVoucherTypeId] = useState('');
  const [partyId, setPartyId] = useState('');
  const [supplierInvoiceNumber, setSupplierInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(today());
  const [bookingDate, setBookingDate] = useState(today());
  const bookingDateManual = useRef(false);
  const [dueDate, setDueDate] = useState('');
  const dueDateManual = useRef(false);
  const [supplier, setSupplier] = useState<PartyDto | null>(null);
  // Role tagging in the picker (ADR-0006 party+role): default = VENDOR-tagged parties only;
  // "search all" widens, and picking a customer offers a one-click promote to BOTH.
  const [searchAllParties, setSearchAllParties] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [purchaseType, setPurchaseType] = useState<PurchaseType>('DOMESTIC');
  const [itcEligibility, setItcEligibility] = useState<ItcEligibility>('ELIGIBLE');
  // §4a addendum: false = one tax row per component (default); true = tax typed per LINE,
  // summed for display, and the ineligible portion costs back to each line's own ledger.
  const [lineTaxCapture, setLineTaxCapture] = useState(false);
  const [costLedgerId, setCostLedgerId] = useState('');
  const [currencyCode, setCurrencyCode] = useState('');
  const [exchangeRate, setExchangeRate] = useState('1');
  const [functionalCurrency, setFunctionalCurrency] = useState('');
  const [manualNumber, setManualNumber] = useState('');
  const [narration, setNarration] = useState('');
  // Supplier debit note only (ADR-0053).
  const [againstInvoiceId, setAgainstInvoiceId] = useState('');
  const [noteReason, setNoteReason] = useState('');
  const againstLabelCache = useRef<Map<string, string>>(new Map());
  const [lines, setLines] = useState<LineRow[]>([blankLine(1)]);
  const lineKey = useRef(1);
  const [taxRows, setTaxRows] = useState<TaxRow[]>([]);
  // ITEM lines + capitalised charges (ADR-0058 slice 3) — bills only; a supplier debit note is refused them.
  const allowItems = !isDebitNote;
  const itemLookups = usePurchaseItemLookups();
  const loadItemDetailsRef = useRef(itemLookups.loadItemDetails); loadItemDetailsRef.current = itemLookups.loadItemDetails;
  // Item lines' tax is computed (tax preview) and pre-filled per line; a line the user edits keeps its figures.
  const [taxPreview, setTaxPreview] = useState<TaxPreviewState | null>(null);
  const previewSeq = useRef(0);
  /** The user chose the eligibility themselves — the preview no longer defaults it. */
  const eligibilityManual = useRef(false);
  /** One-line notice after the first item line switched the capture to per line. */
  const [captureNotice, setCaptureNotice] = useState<string | null>(null);
  const [mismatchMessage, setMismatchMessage] = useState<string | null>(null);

  const [voucherTypes, setVoucherTypes] = useState<VoucherTypeDto[]>([]);
  const [components, setComponents] = useState<TaxComponentDto[]>([]);
  // Jurisdiction's input-tax rules (§4a): component sets (IN_GST: CGST+SGST vs IGST) + ITC
  // preconditions — pack-metadata stand-in, enforced generically here and on the server.
  const [taxRules, setTaxRules] = useState<InputTaxRulesDto | null>(null);
  /** The picked supplier's registration state under the entity's jurisdiction. */
  const [supplierRegState, setSupplierRegState] = useState<'registered' | 'unregistered' | 'unknown' | null>(null);
  const partyCache = useRef<Map<string, { label: string; currency?: string | null; partyType?: string }>>(new Map());
  const currencyLabelCache = useRef<Map<string, string>>(new Map());
  const ledgerCache = useRef<Map<string, string>>(new Map());

  const [duplicates, setDuplicates] = useState<SupplierDuplicateProbe[]>([]);
  const [descFocusKey, setDescFocusKey] = useState<number | null>(null);
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const initialSnapshotRef = useRef<string>('');

  const seedTaxRows = useCallback((comps: TaxComponentDto[], src?: PurchaseInvoiceDto | null): TaxRow[] => {
    const bySrc = new Map((src?.taxes || []).map(t => [t.componentCode.toUpperCase(), t]));
    return comps.map(c => {
      const s = bySrc.get(c.componentCode.toUpperCase());
      return {
        componentCode: c.componentCode,
        componentName: c.componentName,
        taxAmount: s ? String(s.taxAmount) : '',
        eligibleAmount: s ? String(s.eligibleAmount) : '',
      };
    });
  }, []);

  const loadLookups = useCallback(async () => {
    try {
      const [vt, comps, rules] = await Promise.all([
        // A supplier debit note numbers from the debit-note series only; a bill never from them (ADR-0053).
        voucherTypeService.getAll({ voucherClass: 'PURCHASE_INVOICE', documentKind: kindRef.current === 'DEBIT_NOTE' ? 'DEBIT_NOTE' : 'DEFAULT', pageSize: 100 }),
        taxMetaService.getComponents().catch(() => [] as TaxComponentDto[]),
        taxMetaService.getInputTaxRules().catch(() => null),
      ]);
      setVoucherTypes(vt.data);
      // One debit-note series to choose from — choose it, and count it as the starting state.
      if (kindRef.current === 'DEBIT_NOTE' && vt.data.length === 1 && modeRef.current !== 'edit') {
        const only = vt.data[0].id;
        setVoucherTypeId(prev => {
          if (prev) return prev;
          try {
            const snap = JSON.parse(initialSnapshotRef.current);
            if (!snap.voucherTypeId) { snap.voucherTypeId = only; initialSnapshotRef.current = JSON.stringify(snap); }
          } catch { /* snapshot not initialised yet */ }
          return only;
        });
      }
      setComponents(comps);
      setTaxRules(rules);
      // Seed the capture rows once the pack's components are known (keep typed amounts on refresh).
      setTaxRows(prev => prev.length ? prev : seedTaxRows(comps, invoiceRef.current));
    } catch {
      setVoucherTypes([]); setComponents([]);
    }
  }, [seedTaxRows]);
  useEffect(() => { if (isOpen) loadLookups(); }, [isOpen, loadLookups]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; return; }
    lineKey.current = 1; dueDateManual.current = false; bookingDateManual.current = false;
    setErrors({}); setDuplicates([]); setHeaderCollapsed(false); setMaximized(false); setSearchAllParties(false); setSupplierRegState(null);
    setTaxPreview(null); setCaptureNotice(null); setMismatchMessage(null);
    // An existing bill's (or its copy's) eligibility is a choice already made.
    eligibilityManual.current = modeRef.current !== 'create';

    const inv = invoiceRef.current;
    const seedLines = (src: PurchaseInvoiceDto): LineRow[] => {
      const keyOfLineNo = new Map((src.lines || []).map((l, idx) => [l.lineNo, idx + 1]));
      return (src.lines || []).map((l, idx) => {
        if (l.expenseLedgerId) ledgerCache.current.set(l.expenseLedgerId, `${l.expenseLedgerCode || ''} - ${l.expenseLedgerName || ''}`);
        // LINE capture: rehydrate this line's tax amounts from the captured rows (keyed by line_no).
        const taxes: LineTaxes = {};
        (src.taxes || []).filter(t => t.lineNo === l.lineNo).forEach(t => {
          taxes[t.componentCode] = { taxAmount: String(t.taxAmount), eligibleAmount: String(t.eligibleAmount) };
        });
        const item = itemFieldsOf(l);
        const capitalised = item.lineType === 'EXPENSE' && !!l.addToStockCost;
        const basis: ChargeAllocationBasis = (l.allocationBasis as ChargeAllocationBasis) || 'VALUE';
        const manualShares = capitalised && basis === 'MANUAL'
          ? rehydrateManualShares(l.lineAmount, (src.chargeAllocations || []).filter(a => a.chargeLineNo === l.lineNo), keyOfLineNo)
          : {};
        return {
          key: idx + 1, description: l.description || '', expenseLedgerId: l.expenseLedgerId || '', quantity: l.quantity != null ? String(l.quantity) : '', rate: l.rate != null ? String(l.rate) : '', lineAmount: String(l.lineAmount), taxes,
          ...item, addToStockCost: capitalised, allocationBasis: basis, manualShares,
          // A saved line's tax is what the supplier printed — recalculation never overwrites it unasked.
          taxEdited: item.lineType === 'ITEM',
        };
      });
    };
    if ((modeRef.current === 'edit' || modeRef.current === 'copy') && inv) {
      const editing = modeRef.current === 'edit';
      const rows = seedLines(inv);
      lineKey.current = Math.max(1, rows.length);
      const linesToSet = rows.length ? rows : [blankLine(1)];
      void loadItemDetailsRef.current(rows.map(r => r.itemId));
      setEditId(editing ? inv.id : null); setEditRowVersion(editing ? inv.rowVersion : 0);
      setVoucherTypeId(inv.voucherTypeId || '');
      setPartyId(inv.partyId); partyCache.current.set(inv.partyId, { label: inv.partyName || '', currency: inv.currencyCode });
      setSupplierInvoiceNumber(editing ? (inv.supplierInvoiceNumber || '') : '');
      setInvoiceDate(editing ? (inv.invoiceDate || '').slice(0, 10) : today());
      setBookingDate(editing ? (inv.bookingDate || '').slice(0, 10) : docToday()); bookingDateManual.current = editing;
      setDueDate(editing ? (inv.dueDate || '').slice(0, 10) : ''); dueDateManual.current = editing;
      setPurchaseType(inv.purchaseType || 'DOMESTIC');
      setItcEligibility(inv.itcEligibility || 'ELIGIBLE');
      setLineTaxCapture(inv.taxCaptureLevel === 'LINE');
      setCostLedgerId(inv.taxes?.find(t => t.costLedgerId)?.costLedgerId || '');
      inv.taxes?.forEach(t => { if (t.costLedgerId) ledgerCache.current.set(t.costLedgerId, t.costLedgerName || ''); });
      setCurrencyCode(inv.currencyCode || ''); setExchangeRate(String(inv.exchangeRate ?? 1));
      setManualNumber(editing ? (inv.invoiceNumber || '') : ''); setNarration(inv.narration || '');
      setAgainstInvoiceId(editing ? (inv.againstInvoiceId || '') : ''); setNoteReason(editing ? (inv.noteReason || '') : '');
      if (editing && inv.againstInvoiceId) againstLabelCache.current.set(inv.againstInvoiceId, inv.againstInvoiceNumber || '');
      setLines(linesToSet);
      setTaxRows(components.length ? seedTaxRows(components, inv) : []);
      initialSnapshotRef.current = JSON.stringify({
        againstInvoiceId: editing ? (inv.againstInvoiceId || '') : '', noteReason: editing ? (inv.noteReason || '') : '',
        voucherTypeId: inv.voucherTypeId || '', partyId: inv.partyId,
        supplierInvoiceNumber: editing ? (inv.supplierInvoiceNumber || '') : '',
        invoiceDate: editing ? (inv.invoiceDate || '').slice(0, 10) : today(),
        bookingDate: editing ? (inv.bookingDate || '').slice(0, 10) : docToday(),
        dueDate: editing ? (inv.dueDate || '').slice(0, 10) : '',
        purchaseType: inv.purchaseType, itcEligibility: inv.itcEligibility,
        currencyCode: inv.currencyCode || '', exchangeRate: String(inv.exchangeRate ?? 1),
        manualNumber: editing ? (inv.invoiceNumber || '') : '', narration: inv.narration || '', lines: linesToSet,
      });
      entityAccountingConfigService.getConfig().then(c => setFunctionalCurrency(c.functionalCurrencyCode || '')).catch(() => {});
      partyService.getById(inv.partyId).then(p => {
        setSupplier(p);
        if (!editing && !dueDateManual.current) setDueDate(addDays(today(), p.creditDays || 0));
      }).catch(() => setSupplier(null));
    } else {
      const createLines = [blankLine(1)];
      // A supplier debit note recorded from a bill starts from that bill's supplier, purchase type,
      // ITC position and currency (ADR-0053); the lines are the NEW value, so they start blank.
      const src = kindRef.current === 'DEBIT_NOTE' ? addsToRef.current : null;
      setEditId(null); setEditRowVersion(0);
      setVoucherTypeId(''); setPartyId(src?.partyId || ''); setSupplierInvoiceNumber('');
      setInvoiceDate(today()); setBookingDate(docToday()); setDueDate(''); setSupplier(null);
      setPurchaseType(src?.purchaseType || 'DOMESTIC'); setItcEligibility(src?.itcEligibility || 'ELIGIBLE'); setLineTaxCapture(false); setCostLedgerId('');
      setCurrencyCode(src?.currencyCode || ''); setExchangeRate(String(src?.exchangeRate ?? 1)); setManualNumber(''); setNarration(''); setLines(createLines);
      setAgainstInvoiceId(src?.id || ''); setNoteReason('');
      if (src) {
        partyCache.current.set(src.partyId, { label: src.partyName || '', currency: src.currencyCode });
        againstLabelCache.current.set(src.id, src.invoiceNumber || '');
        partyService.getById(src.partyId).then(setSupplier).catch(() => setSupplier(null));
      }
      setTaxRows(components.length ? seedTaxRows(components) : []);
      initialSnapshotRef.current = JSON.stringify({
        againstInvoiceId: src?.id || '', noteReason: '',
        voucherTypeId: '', partyId: src?.partyId || '', supplierInvoiceNumber: '', invoiceDate: today(), bookingDate: docToday(), dueDate: '',
        purchaseType: src?.purchaseType || 'DOMESTIC', itcEligibility: src?.itcEligibility || 'ELIGIBLE', currencyCode: src?.currencyCode || '', exchangeRate: String(src?.exchangeRate ?? 1),
        manualNumber: '', narration: '', lines: createLines,
      });
      entityAccountingConfigService.getConfig().then(c => {
        const fc = c.functionalCurrencyCode || '';
        setFunctionalCurrency(fc);
        setCurrencyCode(prev => {
          const next = prev || fc;
          try { const snap = JSON.parse(initialSnapshotRef.current); snap.currencyCode = next; initialSnapshotRef.current = JSON.stringify(snap); } catch { /* restore path */ }
          return next;
        });
      }).catch(() => {});
    }
  }, [isOpen]);

  const selectedVt = voucherTypes.find(v => v.id === voucherTypeId);
  const showManualNumber = !!selectedVt && (selectedVt.numberingMethod === 'MANUAL' || selectedVt.allowManualEntry);

  const loadParties = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const r = await partyService.getAll({
        pageSize: 50, searchTerm: input || undefined,
        // VENDOR-tagged by default (SUPPLIER or BOTH — the server widens BOTH itself).
        partyType: searchAllParties ? undefined : 'SUPPLIER',
      });
      r.data.forEach(p => partyCache.current.set(p.id, { label: `${p.partyCode} - ${p.partyName}`, currency: p.currencyCode, partyType: p.partyType }));
      return r.data.map(p => ({ value: p.id, label: `${p.partyCode} - ${p.partyName}${p.partyType === 'CUSTOMER' ? '  (customer)' : ''}` }));
    } catch { return []; }
  }, [searchAllParties]);
  const loadCurrencies = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const list = await currencyService.getAll(input || undefined);
      return list.map(c => {
        const label = `${c.currencyCode} — ${c.currencyName}`;
        currencyLabelCache.current.set(c.currencyCode, label);
        return { value: c.currencyCode, label };
      });
    } catch { return []; }
  }, []);
  const loadLedgers = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await coaLedgerService.getAll({ pageSize: 50, searchTerm: input || undefined });
      res.data.forEach(l => ledgerCache.current.set(l.id, `${l.ledgerCode} - ${l.ledgerName}`));
      return res.data.map(l => ({ value: l.id, label: `${l.ledgerCode} - ${l.ledgerName}` }));
    } catch { return []; }
  }, []);

  // Supplier debit note: the supplier's posted bills it may add to (never another debit note).
  const loadAddable = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const r = await purchaseInvoiceService.getAll({ documentKind: 'INVOICE', partyId: partyId || undefined, searchTerm: input || undefined, pageSize: 50 });
      return r.data.map(i => {
        const label = `${i.invoiceNumber}${i.supplierInvoiceNumber ? ` (${i.supplierInvoiceNumber})` : ''} · ${(i.invoiceDate || '').slice(0, 10)} · ${i.currencyCode} ${money(i.grandTotal)}${partyId ? '' : ` · ${i.partyName}`}`;
        againstLabelCache.current.set(i.id, label);
        return { value: i.id, label };
      });
    } catch { return []; }
  }, [partyId]);
  /** Picking the bill a note adds to carries its supplier, purchase type, ITC position and currency over. */
  const onPickAddsTo = (id: string) => {
    setAgainstInvoiceId(id);
    if (!id) return;
    purchaseInvoiceService.getById(id).then(src => {
      if (src.partyId !== partyId) {
        partyCache.current.set(src.partyId, { label: src.partyName || '', currency: src.currencyCode });
        setPartyId(src.partyId);
        partyService.getById(src.partyId).then(p => {
          setSupplier(p);
          if (!dueDateManual.current) setDueDate(addDays(invoiceDate, p.creditDays || 0));
        }).catch(() => setSupplier(null));
      }
      setPurchaseType(src.purchaseType || 'DOMESTIC');
      setItcEligibility(src.itcEligibility || 'ELIGIBLE');
      if (src.currencyCode && src.currencyCode !== currencyCode) {
        setCurrencyCode(src.currencyCode);
        setExchangeRate(src.currencyCode === functionalCurrency ? '1' : String(src.exchangeRate ?? ''));
      }
    }).catch(() => notificationService.toast.error('Could not load the bill.'));
  };
  const onPickCurrency = (code: string) => {
    setCurrencyCode(code);
    setExchangeRate(code === functionalCurrency ? '1' : '');
    setErrors(prev => { const { currencyCode: _c, exchangeRate: _r, ...rest } = prev; return rest; });
  };

  const onPickParty = (id: string) => {
    setPartyId(id);
    // The bill a supplier debit note adds to belongs to one supplier; another supplier drops it.
    if (isDebitNote && againstInvoiceId && id !== partyId) setAgainstInvoiceId('');
    setDuplicates([]);
    setSupplierRegState(null);
    const cur = partyCache.current.get(id)?.currency || functionalCurrency;
    setCurrencyCode(cur);
    if (cur === functionalCurrency) setExchangeRate('1');
    if (!id) { setSupplier(null); return; }
    partyService.getById(id).then(p => {
      setSupplier(p);
      if (!dueDateManual.current) setDueDate(addDays(invoiceDate, p.creditDays || 0));
    }).catch(() => setSupplier(null));
    // ITC precondition (§4a rules): where the regime grants credit only on a registered
    // supplier's invoice, an unregistered supplier WARNS and defaults the eligibility to
    // Ineligible — never blocks (our profile may be stale; the document is authoritative).
    if (taxRules?.itcRequiresRegisteredSupplier) {
      taxProfileService.getPartyProfile(id).then(p => {
        if (!p) { setSupplierRegState('unknown'); return; }
        const registered = !!p.isRegistered && !!(p.registrationNumber || '').trim();
        setSupplierRegState(registered ? 'registered' : 'unregistered');
        if (!registered) setItcEligibility('INELIGIBLE');
      }).catch(() => setSupplierRegState('unknown'));
    }
  };
  /** Tag a customer as supplier too (party_type → BOTH) so it lands in the vendor picker for good. */
  const promoteToSupplier = async () => {
    if (!supplier) return;
    setPromoting(true);
    try {
      await partyService.update(supplier.id, {
        partyCode: supplier.partyCode, partyName: supplier.partyName, legalName: supplier.legalName,
        partyType: 'BOTH', partyGroupId: supplier.partyGroupId,
        email: supplier.email, phone: supplier.phone,
        countryId: supplier.countryId, countryName: supplier.countryName, stateName: supplier.stateName,
        addressLine1: supplier.addressLine1, city: supplier.city, postalCode: supplier.postalCode,
        currencyCode: supplier.currencyCode, creditDays: supplier.creditDays, notes: supplier.notes,
        statusCode: supplier.statusCode || 'ACTIVE',
        rowVersion: supplier.rowVersion,
      });
      const refreshed = await partyService.getById(supplier.id);
      setSupplier(refreshed);
      partyCache.current.set(refreshed.id, { label: `${refreshed.partyCode} - ${refreshed.partyName}`, currency: refreshed.currencyCode, partyType: refreshed.partyType });
      notificationService.toast.success(`${refreshed.partyName} is now tagged as a supplier.`);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not tag the party as a supplier.');
    } finally { setPromoting(false); }
  };
  const onChangeInvoiceDate = (d: string) => {
    setInvoiceDate(d);
    if (!bookingDateManual.current) setBookingDate(d < docToday() ? docToday() : d);
    if (!dueDateManual.current) setDueDate(addDays(d, supplier?.creditDays || 0));
  };
  const checkDuplicates = useCallback(async () => {
    const num = supplierInvoiceNumber.trim();
    if (!partyId || !num) { setDuplicates([]); return; }
    try {
      setDuplicates(await purchaseInvoiceService.duplicateCheck(partyId, num, editId || undefined));
    } catch { setDuplicates([]); }
  }, [partyId, supplierInvoiceNumber, editId]);

  const updLine = (key: number, patch: Partial<LineRow>) => setLines(rows => rows.map(r => {
    if (r.key !== key) return r;
    const n = { ...r, ...patch };
    if (patch.quantity !== undefined || patch.rate !== undefined) {
      const q = parseFloat(n.quantity), rt = parseFloat(n.rate);
      if (!isNaN(q) && !isNaN(rt)) n.lineAmount = (q * rt).toFixed(2);
    }
    return n;
  }));
  const addLine = () => setLines(rows => [...rows, blankLine(++lineKey.current)]);
  const removeLine = (key: number) => setLines(rows => rows.length > 1 ? rows.filter(r => r.key !== key) : rows);
  /**
   * Expense ↔ Item: an item line drops the charge option; an expense line drops the item. The
   * bill's FIRST item line switches the tax capture to per line (item tax is computed per line);
   * invoice-level amounts already typed are not carried over, so the user is told to re-check.
   */
  const setLineType = (key: number, lineType: PurchaseLineType) => {
    const firstItemLine = lineType === 'ITEM' && !lines.some(r => r.key !== key && r.lineType === 'ITEM');
    if (firstItemLine && allowItems && !lineTaxCapture) {
      setLineTaxCapture(true);
      if (taxRows.some(t => (parseFloat(t.taxAmount) || 0) !== 0)) setCaptureNotice('Tax is now captured per line; re-check the amounts.');
    }
    setLines(rows => rows.map(r => {
      if (r.key !== key || r.lineType === lineType) return r;
      return lineType === 'ITEM'
        ? { ...r, lineType: 'ITEM', addToStockCost: false, manualShares: {}, taxEdited: false }
        : { ...r, ...blankItemFields(), taxEdited: false };
    }));
  };
  const updTax = (code: string, patch: Partial<TaxRow>) =>
    setTaxRows(rows => rows.map(r => r.componentCode === code ? { ...r, ...patch } : r));
  const updLineTax = (key: number, code: string, patch: Partial<{ taxAmount: string; eligibleAmount: string }>) =>
    setLines(rows => rows.map(r => {
      if (r.key !== key) return r;
      const existing = r.taxes[code] ?? { taxAmount: '', eligibleAmount: '' };
      // Typing a tax amount on an item line pins it — later recalculations leave it alone.
      const pinned = patch.taxAmount !== undefined && r.lineType === 'ITEM' ? { taxEdited: true } : {};
      return { ...r, ...pinned, taxes: { ...r.taxes, [code]: { ...existing, ...patch } } };
    }));
  /** "Recalculate": the line takes the computed figures again and follows later recalculations. */
  const recalcLine = (key: number) => {
    const computed = taxPreview?.byKey[key];
    if (!computed) return;
    setLines(rows => rows.map(r => r.key !== key ? r : { ...r, taxes: computedTaxes(computed, r.taxes), taxEdited: false }));
  };

  // ---- Tax preview (item lines): the engine's tax for each item line, pre-filled per line ----
  // Item lines with an item and an amount, numbered by their position on the form (= "Line N").
  const previewLines = allowItems
    ? lines.map((l, idx) => ({ l, lineNo: idx + 1 }))
        .filter(({ l }) => l.lineType === 'ITEM' && !!l.itemId && (parseFloat(l.lineAmount) || 0) > 0)
    : [];
  const previewSignature = isOpen && allowItems && partyId && bookingDate && previewLines.length
    ? JSON.stringify({ partyId, bookingDate, lines: previewLines.map(({ l, lineNo }) => [lineNo, l.key, l.itemId, parseFloat(l.lineAmount) || 0, parseFloat(l.quantity) || null]) })
    : '';
  const componentsRef = useRef(components); componentsRef.current = components;
  // The signature carries everything the call reads; state setters are stable.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!previewSignature) { previewSeq.current++; setTaxPreview(null); return; }
    const sig = JSON.parse(previewSignature) as { partyId: string; bookingDate: string; lines: [number, number, string, number, number | null][] };
    const seq = ++previewSeq.current;
    const timer = setTimeout(async () => {
      let result: TaxCalculationResult;
      try {
        result = await purchaseInvoiceService.taxPreview({
          partyId: sig.partyId, date: sig.bookingDate,
          lines: sig.lines.map(([lineNo, , itemId, amount, qty]) => ({ lineId: String(lineNo), itemId, taxableAmount: amount, quantity: qty ?? undefined })),
        });
      } catch (err: any) {
        if (seq !== previewSeq.current) return;
        const msg = err?.response?.data?.message || 'Could not calculate the tax for the item lines.';
        setTaxPreview({ result: { success: false, errors: [msg], lines: [] } as unknown as TaxCalculationResult, byKey: {}, lineErrors: {}, errors: [msg] });
        return;
      }
      if (seq !== previewSeq.current) return; // the lines moved on meanwhile
      const keyByLineId = new Map(sig.lines.map(([lineNo, key]) => [String(lineNo), key]));
      // The engine's component codes, spelled as the pack's (the per-line inputs key by those).
      const packCode = (code: string) => componentsRef.current.find(c => c.componentCode.toUpperCase() === code.toUpperCase())?.componentCode || code;
      const byKey: Record<number, ComputedLineTax> = {};
      (result.lines || []).forEach(rl => {
        const key = keyByLineId.get(String(rl.lineId));
        if (key == null) return;
        const comp: ComputedLineTax = {};
        (rl.components || []).forEach(c => {
          const code = packCode(c.componentCode);
          const prev = comp[code];
          comp[code] = { taxAmount: (prev?.taxAmount || 0) + c.taxAmount, taxableBase: c.taxableBase, ratePercentage: (prev?.ratePercentage || 0) + c.ratePercentage };
        });
        byKey[key] = comp;
      });
      const lineErrors: Record<number, string[]> = {};
      const errors: string[] = [];
      (result.errors || []).forEach(e => {
        const m = /Line\s+(\d+)/i.exec(e);
        const key = m ? keyByLineId.get(m[1]) : undefined;
        if (key != null) (lineErrors[key] = lineErrors[key] || []).push(e);
        else errors.push(e);
      });
      setTaxPreview({ result, byKey, lineErrors, errors });
      // Untouched item lines follow the computed figures; a successful run with no answer for a line clears it.
      setLines(rows => rows.map(r => {
        if (r.lineType !== 'ITEM' || r.taxEdited) return r;
        const computed = byKey[r.key];
        if (computed) return { ...r, taxes: computedTaxes(computed, r.taxes) };
        return result.success && Array.from(keyByLineId.values()).includes(r.key) ? { ...r, taxes: {} } : r;
      }));
      // Not registered (either side): the tax is not claimable — default the eligibility, unless chosen.
      const attrs = result.taxAttributes || {};
      if ((attrs.supplierRegistered === false || attrs.registrationStatus === 'NOT_REGISTERED') && !eligibilityManual.current) {
        setItcEligibility('INELIGIBLE');
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [previewSignature]);
  const previewAttrs = taxPreview?.result.taxAttributes || {};
  /** The engine's word on registration (null = no preview / not said). */
  const engineSupplierRegistered = typeof previewAttrs.supplierRegistered === 'boolean' ? previewAttrs.supplierRegistered as boolean : null;
  const previewReason = engineSupplierRegistered === false
    ? 'Supplier not registered — no tax is charged.'
    : previewAttrs.registrationStatus === 'NOT_REGISTERED'
      ? 'This entity is not registered — the tax is a cost.'
      : null;

  // ---- Line ledgers: where each item line posts on the booking date ----
  const lineLedgers = usePurchaseLineLedgers('INVOICE', bookingDate, allowItems ? lines.filter(l => l.lineType === 'ITEM').map(l => l.itemId) : [], isOpen && allowItems);
  const ledgerInfoOf = (l: LineRow): DocumentLineLedgerDto | undefined =>
    allowItems && l.lineType === 'ITEM' && l.itemId ? lineLedgers[l.itemId] : undefined;
  /** The bill's item lines — what a capitalised charge spreads onto. */
  const itemRows = allowItems ? lines.filter(l => l.lineType === 'ITEM' && !!l.itemId) : [];
  const manualAllocated = (l: LineRow) => itemRows.reduce((s, r) => s + (parseFloat(l.manualShares[r.key]) || 0), 0);

  const subtotal = lines.reduce((s, l) => s + (parseFloat(l.lineAmount) || 0), 0);
  // LINE mode sums the per-line amounts; INVOICE mode reads the header rows.
  const lineTaxByComponent: Record<string, { tax: number; eligible: number }> = {};
  if (lineTaxCapture) {
    lines.forEach(l => Object.entries(l.taxes).forEach(([code, v]) => {
      const t = parseFloat(v.taxAmount) || 0;
      const e = Math.min(parseFloat(v.eligibleAmount) || 0, t);
      if (!lineTaxByComponent[code]) lineTaxByComponent[code] = { tax: 0, eligible: 0 };
      lineTaxByComponent[code].tax += t;
      lineTaxByComponent[code].eligible += e;
    }));
  }
  const taxTotal = lineTaxCapture
    ? Object.values(lineTaxByComponent).reduce((s, v) => s + v.tax, 0)
    : taxRows.reduce((s, t) => s + (parseFloat(t.taxAmount) || 0), 0);
  const grandTotal = subtotal + taxTotal;
  const eligibleTotal = itcEligibility === 'ELIGIBLE' ? taxTotal
    : itcEligibility === 'INELIGIBLE' ? 0
    : lineTaxCapture
      ? Object.values(lineTaxByComponent).reduce((s, v) => s + v.eligible, 0)
      : taxRows.reduce((s, t) => s + Math.min(parseFloat(t.eligibleAmount) || 0, parseFloat(t.taxAmount) || 0), 0);
  const toCost = taxTotal - eligibleTotal;

  // Component-set exclusivity (§4a rules, enforced generically): once one set carries an
  // amount, the other sets' inputs disable — across the whole document in either capture mode.
  const setOf = (code: string) =>
    taxRules?.componentSets.find(s => s.componentCodes.some(c => c.toUpperCase() === code.toUpperCase())) ?? null;
  const usedSetCodes = new Set<string>();
  if (taxRules && taxRules.exclusivityScope === 'DOCUMENT' && taxRules.componentSets.length > 0) {
    const usedCodes: string[] = lineTaxCapture
      ? lines.flatMap(l => Object.entries(l.taxes).filter(([, v]) => (parseFloat(v.taxAmount) || 0) > 0).map(([c]) => c))
      : taxRows.filter(t => (parseFloat(t.taxAmount) || 0) > 0).map(t => t.componentCode);
    usedCodes.forEach(c => { const s = setOf(c); if (s) usedSetCodes.add(s.setCode); });
  }
  const componentDisabled = (code: string): string | null => {
    if (usedSetCodes.size !== 1) return null;
    const s = setOf(code);
    if (!s || usedSetCodes.has(s.setCode)) return null;
    const active = taxRules!.componentSets.find(x => usedSetCodes.has(x.setCode));
    return `The document already carries ${active?.setName || 'another treatment'} — one treatment per invoice.`;
  };
  const mixedSetNames = usedSetCodes.size > 1
    ? taxRules!.componentSets.filter(s => usedSetCodes.has(s.setCode)).map(s => s.setName).join(' vs ')
    : null;
  // The tax preview's word on the supplier's registration (when it gave one) supersedes the
  // profile probe — one message, never two contradictory ones.
  const supplierItcWarning = taxRules?.itcRequiresRegisteredSupplier && taxTotal > 0 && engineSupplierRegistered === null
    ? (supplierRegState === 'unregistered'
        ? `This supplier is not registered under ${taxRules.jurisdictionCode || 'the jurisdiction'} — input tax is normally not claimable (an unregistered supplier should not be charging tax at all). Eligibility was defaulted to Ineligible; override if your records are stale.`
        : supplierRegState === 'unknown' && itcEligibility !== 'INELIGIBLE'
        ? 'No tax registration on record for this supplier — check the invoice carries a valid registration before claiming ITC.'
        : null)
    : null;

  const currentSnapshot = useMemo(() => JSON.stringify({
    againstInvoiceId, noteReason,
    voucherTypeId, partyId, supplierInvoiceNumber, invoiceDate, bookingDate, dueDate, purchaseType, itcEligibility,
    lineTaxCapture, costLedgerId, currencyCode, exchangeRate, manualNumber, narration, lines, taxRows,
  }), [againstInvoiceId, noteReason, voucherTypeId, partyId, supplierInvoiceNumber, invoiceDate, bookingDate, dueDate, purchaseType, itcEligibility, lineTaxCapture, costLedgerId, currencyCode, exchangeRate, manualNumber, narration, lines, taxRows]);
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return currentSnapshot !== initialSnapshotRef.current;
  }, [currentSnapshot]);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  const handleSave = () => {
    const e: Record<string, string> = {};
    if (!voucherTypeId) e.voucherTypeId = 'Voucher type is required';
    if (!partyId) e.partyId = 'Supplier is required';
    if (isDebitNote && !noteReason.trim()) e.noteReason = 'Say why the supplier raised the value';
    if (showManualNumber && selectedVt?.numberingMethod === 'MANUAL' && !manualNumber.trim()) e.manualNumber = 'Number is required';
    if (!currencyCode) e.currencyCode = 'Currency is required';
    if (currencyCode && currencyCode !== functionalCurrency && !(parseFloat(exchangeRate) > 0)) {
      e.exchangeRate = `Enter today's rate — 1 ${currencyCode} in ${functionalCurrency}`;
    }
    if (bookingDate && invoiceDate && bookingDate < invoiceDate) e.bookingDate = "Booking date cannot be before the supplier's invoice date";
    const valid = lines.filter(isSubmitted);
    if (valid.length === 0) e.lines = 'Add at least one line with an amount';
    // ITEM lines + capitalised charges (ADR-0058 slice 3) — the server's rules, caught early.
    const lineErrors: string[] = [];
    const validItemRows = valid.filter(l => l.lineType === 'ITEM' && l.itemId);
    valid.forEach((l, idx) => {
      const n = idx + 1;
      if (l.lineType === 'ITEM') {
        if (!l.itemId) { e[`line-${l.key}-item`] = 'Required'; lineErrors.push(`Line ${n}: pick the item`); return; }
        if (!(parseFloat(l.quantity) > 0)) { e[`line-${l.key}-quantity`] = 'Required'; lineErrors.push(`Line ${n}: enter the quantity (more than 0)`); }
        if (itemLookups.itemTypeOf(l) === 'STOCK' && !l.stockLocationId) { e[`line-${l.key}-location`] = 'Required'; lineErrors.push(`Line ${n}: pick the stock location`); }
        return;
      }
      if (!l.addToStockCost || !allowItems) return;
      if (validItemRows.length === 0) { e[`line-${l.key}-charge`] = 'Add to stock cost needs an item line on the bill'; lineErrors.push(`Line ${n}: add to stock cost needs an item line on the bill`); return; }
      if (l.allocationBasis === 'MANUAL') {
        const amount = parseFloat(l.lineAmount) || 0;
        const shares = validItemRows.map(r => parseFloat(l.manualShares[r.key]) || 0);
        const allocated = shares.reduce((s, v) => s + v, 0);
        if (shares.some(v => v < 0)) { e[`line-${l.key}-charge`] = 'Shares cannot be negative'; lineErrors.push(`Line ${n}: manual shares cannot be negative`); }
        else if (Math.abs(allocated - amount) > 0.005) {
          e[`line-${l.key}-charge`] = `Allocated ${money(allocated)} — must be ${money(amount)}`;
          lineErrors.push(`Line ${n}: the manual shares (${money(allocated)}) must add up to the charge (${money(amount)})`);
        }
      }
    });
    if (lineErrors.length && !e.lines) e.lines = lineErrors[0];
    if (mixedSetNames && taxRules?.exclusivitySeverity === 'BLOCK') {
      e.taxes = `These tax components cannot be mixed on one document: ${mixedSetNames}. A supplier's invoice carries one treatment.`;
    }
    // (The tax preview never blocks — its errors are shown beside the tax entry.)
    if (itcEligibility === 'PARTIAL') {
      if (taxTotal <= 0) e.taxes = 'Partially eligible — but no tax is entered';
      else if (lineTaxCapture) {
        outer:
        for (const [idx, l] of valid.entries()) {
          for (const [code, v] of Object.entries(l.taxes)) {
            const t = parseFloat(v.taxAmount) || 0;
            if (t <= 0) continue;
            const el = parseFloat(v.eligibleAmount);
            if (isNaN(el) || el < 0 || el > t) { e.taxes = `Line ${idx + 1} ${code}: enter the eligible amount (0 to ${t})`; break outer; }
          }
        }
      } else {
        for (const t of taxRows.filter(t => parseFloat(t.taxAmount) > 0)) {
          const el = parseFloat(t.eligibleAmount);
          if (isNaN(el) || el < 0 || el > (parseFloat(t.taxAmount) || 0)) { e.taxes = `${t.componentCode}: enter the eligible amount (0 to ${t.taxAmount})`; break; }
        }
      }
    }
    // Per-field line flags (line-<key>-…) only highlight; the toast reads a sentence.
    if (Object.keys(e).length) { setErrors(e); notificationService.toast.warning(Object.entries(e).find(([k]) => !k.startsWith('line-'))?.[1] || Object.values(e)[0]); return; }
    setErrors({});
    // Item lines whose entered tax differs from the computed tax: a warning, never a block.
    const mismatches: string[] = [];
    if (lineTaxCapture && taxPreview) {
      valid.forEach((l, idx) => {
        const computed = l.lineType === 'ITEM' ? taxPreview.byKey[l.key] : undefined;
        if (!computed) return;
        const codes = Array.from(new Set([...Object.keys(computed), ...Object.keys(l.taxes)]));
        codes.forEach(code => {
          const c = computed[code]?.taxAmount || 0;
          const entered = parseFloat(l.taxes[code]?.taxAmount) || 0;
          if (Math.abs(c - entered) > 0.005) mismatches.push(`Line ${idx + 1} · ${l.itemName || l.itemText || 'Item'} · ${code}: computed ${money(c)}, entered ${money(entered)}`);
        });
      });
    }
    if (mismatches.length) {
      const shown = mismatches.slice(0, 6).join('; ') + (mismatches.length > 6 ? `; and ${mismatches.length - 6} more` : '');
      setMismatchMessage(`The tax entered differs from the computed tax — ${shown}. Save with the entered figures?`);
      return;
    }
    setConfirmOpen(true);
  };
  const handleConfirmSave = async () => {
    setConfirmOpen(false); setSaving(true);
    try {
      // Filtered submit order defines the server's line numbering — LINE-mode tax rows
      // reference their line by that ordinal.
      const submitLines = lines.filter(isSubmitted);
      // MANUAL charge shares reference item lines by that same ordinal.
      const lineNoByKey = new Map(submitLines.map((l, idx) => [l.key, idx + 1]));
      const submitItemRows = allowItems ? submitLines.filter(l => l.lineType === 'ITEM' && l.itemId) : [];
      const toLineRequest = (l: LineRow): CreatePurchaseInvoiceLineRequest => {
        // ITEM lines: a goods ledger is fixed (no ledger sent); otherwise the pick, else the pre-fill.
        const ledgerId = allowItems && l.lineType === 'ITEM' ? effectiveItemLedgerId(l.expenseLedgerId, ledgerInfoOf(l)) : l.expenseLedgerId;
        const common = {
          expenseLedgerId: ledgerId || undefined,
          quantity: l.quantity ? parseFloat(l.quantity) : undefined, rate: l.rate ? parseFloat(l.rate) : undefined,
          lineAmount: parseFloat(l.lineAmount) || 0,
        };
        if (allowItems && l.lineType === 'ITEM') {
          return {
            lineType: 'ITEM', ...common,
            // Blank description → the item's name.
            description: l.description.trim() || l.itemName || undefined,
            itemId: l.itemId, uomId: l.uomId || undefined,
            stockLocationId: itemLookups.itemTypeOf(l) === 'NON_STOCK' ? undefined : (l.stockLocationId || undefined),
          };
        }
        const capitalise = allowItems && l.addToStockCost && submitItemRows.length > 0;
        return {
          lineType: 'EXPENSE', ...common,
          description: l.description.trim() || undefined,
          ...(capitalise ? {
            addToStockCost: true,
            allocationBasis: l.allocationBasis,
            manualShares: l.allocationBasis === 'MANUAL'
              ? Object.fromEntries(submitItemRows
                  .map(r => [lineNoByKey.get(r.key)!, parseFloat(l.manualShares[r.key]) || 0] as [number, number])
                  .filter(([, v]) => v > 0))
              : undefined,
          } : {}),
        };
      };
      const taxes: CreatePurchaseInvoiceTaxRequest[] = lineTaxCapture
        ? submitLines.flatMap((l, idx) => Object.entries(l.taxes)
            .filter(([, v]) => (parseFloat(v.taxAmount) || 0) > 0)
            .map(([code, v]) => ({
              lineNo: idx + 1,
              componentCode: code,
              taxAmount: parseFloat(v.taxAmount) || 0,
              eligibleAmount: itcEligibility === 'PARTIAL' ? (parseFloat(v.eligibleAmount) || 0) : undefined,
              // LINE mode: the server costs the ineligible portion to the LINE's own ledger.
            })))
        : taxRows.filter(t => parseFloat(t.taxAmount) > 0).map(t => ({
            componentCode: t.componentCode,
            taxAmount: parseFloat(t.taxAmount) || 0,
            eligibleAmount: itcEligibility === 'PARTIAL' ? (parseFloat(t.eligibleAmount) || 0) : undefined,
            costLedgerId: itcEligibility !== 'ELIGIBLE' ? (costLedgerId || undefined) : undefined,
          }));
      const payload: CreatePurchaseInvoiceRequest = {
        voucherTypeId, partyId,
        supplierInvoiceNumber: supplierInvoiceNumber.trim() || undefined,
        invoiceDate, bookingDate: bookingDate || undefined, dueDate: dueDate || undefined,
        purchaseType, itcEligibility,
        taxCaptureLevel: lineTaxCapture ? 'LINE' : 'INVOICE',
        currencyCode, exchangeRate: parseFloat(exchangeRate) || 1,
        manualNumber: (selectedVt?.numberingMethod === 'MANUAL' && manualNumber.trim()) ? manualNumber.trim() : undefined,
        narration: narration.trim() || undefined,
        againstInvoiceId: isDebitNote ? (againstInvoiceId || null) : undefined,
        noteReason: isDebitNote ? noteReason.trim() : undefined,
        lines: submitLines.map(toLineRequest),
        taxes,
      };
      const saved = editId
        ? await purchaseInvoiceService.update(editId, { ...payload, rowVersion: editRowVersion })
        : await purchaseInvoiceService.create(payload);
      setSaving(false);
      loadLookups();
      notificationService.toast.success(`${docLabel} ${saved.invoiceNumber || ''} ${editId ? 'updated' : 'posted'}.`);
      onSaved(); onClose();
    } catch (err: any) { setSaving(false); notificationService.toast.error(err?.response?.data?.message || `Could not ${editId ? 'update' : 'post'} the ${docLabel.toLowerCase()}.`); }
  };

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentMode = modeRef.current;
    const editing = currentMode === 'edit';
    const copying = currentMode === 'copy';
    const cId = createInstanceIdRef.current || '1';
    const cpId = copyInstanceIdRef.current || '1';
    const sourceInvoiceNo = invoiceRef.current?.invoiceNumber;
    const snap = { editId, editRowVersion, voucherTypeId, partyId, supplierInvoiceNumber, invoiceDate, bookingDate, bookingDateManual: bookingDateManual.current, dueDate, dueDateManual: dueDateManual.current, supplier, purchaseType, itcEligibility, lineTaxCapture, costLedgerId, currencyCode, exchangeRate, functionalCurrency, manualNumber, narration, againstInvoiceId, noteReason, lines: lines.map(l => ({ ...l, taxes: { ...l.taxes }, manualShares: { ...l.manualShares } })), taxRows: taxRows.map(t => ({ ...t })) };
    const savedInitialSnapshot = initialSnapshotRef.current;
    const snapKind = kindRef.current;
    const currentIsDirty = savedInitialSnapshot ? currentSnapshot !== savedInitialSnapshot : false;
    // A supplier debit note draft gets its own id space so it never collides with a bill draft.
    const idKind = snapKind === 'DEBIT_NOTE' ? 'sdn-' : '';
    const modalId = editing
      ? `${tabId}-form-modal-edit-${idKind}${manualNumber || editId || ''}`
      : copying
      ? `${tabId}-form-modal-copy-${cpId}`
      : `${tabId}-form-modal-create-${idKind}${cId}`;
    const title = editing
      ? `Edit ${docLabel} - ${manualNumber || ''}`
      : copying
      ? `Copy Bill - ${sourceInvoiceNo || 'draft'} (${cpId})`
      : partyId
      ? `${docLabel} - ${partyCache.current.get(partyId)?.label || ''} (${cId})`
      : `New ${docLabel} (${cId})`;
    minimize({
      id: modalId, title, moduleName, tabId, isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setEditId(snap.editId); setEditRowVersion(snap.editRowVersion);
        setVoucherTypeId(snap.voucherTypeId); setPartyId(snap.partyId); setSupplierInvoiceNumber(snap.supplierInvoiceNumber);
        setInvoiceDate(snap.invoiceDate); setBookingDate(snap.bookingDate); bookingDateManual.current = snap.bookingDateManual;
        setDueDate(snap.dueDate); dueDateManual.current = snap.dueDateManual;
        setSupplier(snap.supplier);
        setPurchaseType(snap.purchaseType); setItcEligibility(snap.itcEligibility); setLineTaxCapture(snap.lineTaxCapture); setCostLedgerId(snap.costLedgerId);
        setCurrencyCode(snap.currencyCode); setExchangeRate(snap.exchangeRate); setFunctionalCurrency(snap.functionalCurrency);
        setManualNumber(snap.manualNumber); setNarration(snap.narration); setLines(snap.lines); setTaxRows(snap.taxRows);
        void loadItemDetailsRef.current(snap.lines.map(l => l.itemId));
        setAgainstInvoiceId(snap.againstInvoiceId); setNoteReason(snap.noteReason);
        initialSnapshotRef.current = savedInitialSnapshot;
        loadLookups();
        onRestoreModal?.(
          editing ? 'edit' : copying ? 'copy' : 'create',
          editing ? undefined : copying ? cpId : cId,
          editing ? (snap.manualNumber || undefined) : copying ? (sourceInvoiceNo || undefined) : undefined,
          snapKind,
        );
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal, editId, editRowVersion, voucherTypeId, partyId, supplierInvoiceNumber, invoiceDate, bookingDate, dueDate, supplier, purchaseType, itcEligibility, lineTaxCapture, costLedgerId, currencyCode, exchangeRate, functionalCurrency, manualNumber, narration, againstInvoiceId, noteReason, docLabel, lines, taxRows, currentSnapshot, loadLookups]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  if (!isOpen) return null;
  const supplierIsCustomerOnly = supplier != null && supplier.partyType === 'CUSTOMER';
  return (
    <>
      <div className="modal-backdrop" />
      <div className={`modal-container ${maximized ? '' : 'modal-xl'}`} style={maximized ? { width: '98vw', maxWidth: '98vw', height: '96vh', maxHeight: '96vh' } : undefined}>
        {saving && <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}><div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>}
        <div className="modal-header" style={{ minHeight: 'auto' }}><h2 className="modal-title">{editId ? `Edit ${docLabel} ${manualNumber || ''}` : isCopy ? `Copy Bill${invoice ? ` — from ${invoice.invoiceNumber}` : ''}` : isDebitNote ? 'Record Supplier Debit Note' : 'New Purchase Invoice (Bill)'}</h2><div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}><button className="modal-header-btn" onClick={() => setMaximized(m => !m)} title={maximized ? 'Restore' : 'Maximize'} disabled={saving}>{maximized ? <IconSystem.windowRestore size={16} /> : <IconSystem.maximize size={16} />}</button>{tabId && <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving}><IconSystem.chevronDown size={16} /></button>}<button className="modal-close" onClick={handleClose} disabled={saving}><IconSystem.x size={20} /></button></div></div>
        <div className="modal-content">
          {editId && (invoice?.debitNotes?.length ?? 0) > 0 && (
            <div style={{ marginBottom: '0.6rem', padding: '0.45rem 0.7rem', borderRadius: 6, background: 'rgba(37,99,235,0.07)', border: '1px solid rgba(37,99,235,0.3)', fontSize: '0.8rem' }}>
              Debit note{invoice!.debitNotes!.length > 1 ? 's' : ''} {invoice!.debitNotes!.map(n => n.debitNoteNumber).join(', ')} adjust this bill. Saving re-applies them to the edited bill
              (any part beyond its unpaid balance goes on account). The bill cannot go below what they reverse: <strong>{money(invoice!.debitNotes!.reduce((s, n) => s + n.grandTotal, 0))}</strong>
              {' '}(tax {money(invoice!.debitNotes!.reduce((s, n) => s + n.taxTotal, 0))}).
            </div>
          )}
          <div onClick={() => setHeaderCollapsed(c => !c)} role="button" tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setHeaderCollapsed(c => !c); } }}
            style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', padding: '0.15rem 0', marginBottom: headerCollapsed ? '0.4rem' : '0.5rem' }}>
            <span className="form-label" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <IconSystem.chevronDown size={14} style={{ transform: headerCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 0.12s' }} />
              {docLabel} Details
            </span>
            {headerCollapsed && <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {(partyId && partyCache.current.get(partyId)?.label) || 'No supplier'} · {invoiceDate}{supplierInvoiceNumber ? ` · ${supplierInvoiceNumber}` : ''}{currencyCode ? ` · ${currencyCode}` : ''}
            </span>}
          </div>
          {!headerCollapsed && (<>
          <div className="form-grid-3">
            <div className="form-group"><SyutSelect label={<>Voucher Type <span className="text-danger">*</span></>} options={voucherTypes.map(v => ({ value: v.id, label: `${v.voucherCode} - ${v.voucherName}` }))} value={voucherTypeId} onChange={v => setVoucherTypeId((v as string) || '')} isClearable={false} isDisabled={!!editId} menuPortalTarget={document.body} error={errors.voucherTypeId} /></div>
            <div className="form-group">
              <SyutSelect key={String(searchAllParties)} label={<>Supplier <span className="text-danger">*</span></>} isAsync loadOptions={loadParties} defaultOptions cacheOptions={false}
                seedOptions={partyId ? [{ value: partyId, label: partyCache.current.get(partyId)?.label || '' }] : undefined}
                menuPortalTarget={document.body} value={partyId} onChange={v => onPickParty((v as string) || '')} placeholder={searchAllParties ? 'Search all parties' : 'Select supplier'} error={errors.partyId} />
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.2rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={searchAllParties} onChange={e => setSearchAllParties(e.target.checked)} />
                Supplier not listed? Search all parties
              </label>
            </div>
            <DocumentNumberField label="Our No." voucherTypeId={voucherTypeId} date={bookingDate}
              existingNumber={editId ? manualNumber : ''} manualValue={manualNumber} onManualChange={setManualNumber}
              required error={errors.manualNumber} />
            <div className="form-group" data-field="supplierInvoiceNumber">
              <label className="form-label">{isDebitNote ? "Supplier's Debit Note No." : 'Supplier Invoice No.'}</label>
              <input className="form-input" value={supplierInvoiceNumber} maxLength={50}
                onChange={e => setSupplierInvoiceNumber(e.target.value)} onBlur={checkDuplicates}
                placeholder="The supplier's own number" />
              {duplicates.length > 0 && (
                <div style={{ fontSize: '0.72rem', color: '#d97706', marginTop: '0.2rem' }}>
                  ⚠ Already recorded for this supplier: {duplicates.slice(0, 3).map(d => `${d.invoiceNumber} (${(d.invoiceDate || '').slice(0, 10)}, ${money(d.grandTotal)})`).join('; ')}{duplicates.length > 3 ? '…' : ''}. Posting is still allowed — check it is not the same bill.
                </div>
              )}
            </div>
            {isDebitNote && (
              <div className="form-group">
                <SyutSelect label="Adds to Bill" isAsync key={`adds-to-${partyId}`} loadOptions={loadAddable} defaultOptions cacheOptions={false}
                  seedOptions={againstInvoiceId ? [{ value: againstInvoiceId, label: againstLabelCache.current.get(againstInvoiceId) || '' }] : undefined}
                  value={againstInvoiceId} onChange={v => onPickAddsTo((v as string) || '')} isClearable
                  menuPortalTarget={document.body} placeholder="Optional — the bill this note adds to" />
              </div>
            )}
            <div className="form-group"><label className="form-label">{isDebitNote ? "Supplier's Note Date" : 'Supplier Invoice Date'} <span className="text-danger">*</span></label><input type="date" className="form-input" value={invoiceDate} onChange={e => onChangeInvoiceDate(e.target.value)} /></div>
            <div className="form-group" data-field="bookingDate"><label className="form-label">Booking Date <span className="text-danger">*</span></label><FyDateInput className={`form-input${errors.bookingDate ? ' form-field-error' : ''}`} value={bookingDate} onChange={v => { bookingDateManual.current = true; setBookingDate(v); }} />{errors.bookingDate ? <div className="form-error-message">{errors.bookingDate}</div> : <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>Drives the posting period</div>}</div>
            <div className="form-group"><label className="form-label">Due Date{supplier?.creditDays ? <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> · {supplier.creditDays}d credit</span> : null}</label><input type="date" className="form-input" value={dueDate} onChange={e => { dueDateManual.current = true; setDueDate(e.target.value); }} /></div>
            <div className="form-group"><SyutSelect label="Purchase Type" options={PURCHASE_TYPE_OPTIONS} value={purchaseType} onChange={v => setPurchaseType(((v as string) || 'DOMESTIC') as PurchaseType)} isClearable={false} menuPortalTarget={document.body} /></div>
            <div className="form-group" data-field="currencyCode">
              <SyutSelect
                label="Currency"
                isAsync defaultOptions cacheOptions={false}
                loadOptions={loadCurrencies}
                seedOptions={currencyCode ? [{ value: currencyCode, label: currencyLabelCache.current.get(currencyCode) || currencyCode }] : undefined}
                value={currencyCode}
                onChange={v => onPickCurrency((v as string) || '')}
                menuPortalTarget={document.body}
                isClearable={false}
                error={errors.currencyCode}
              />
            </div>
            {currencyCode && currencyCode !== functionalCurrency && (
              <div className="form-group" data-field="exchangeRate">
                <label className="form-label">Exchange Rate <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>→ {functionalCurrency}</span> <span className="text-danger">*</span></label>
                <input type="number" step="0.000001" min="0"
                  className={`form-input${errors.exchangeRate ? ' form-field-error' : ''}`}
                  value={exchangeRate}
                  onChange={e => setExchangeRate(e.target.value)}
                  placeholder={`1 ${currencyCode} = ? ${functionalCurrency}`} />
                {errors.exchangeRate && <div className="form-error-message">{errors.exchangeRate}</div>}
              </div>
            )}
          </div>

          {supplier && (
            <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', borderRadius: 6, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', fontSize: '0.8rem', display: 'flex', flexWrap: 'wrap', gap: '0.25rem 1.5rem', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>Supplier: {supplier.partyName}</span>
              {formatAddress(supplier) && <span style={{ color: 'var(--text-secondary)' }}>{formatAddress(supplier)}</span>}
              {supplierIsCustomerOnly && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="badge" style={{ background: '#d97706', color: '#fff' }}>Tagged customer only</span>
                  <button className="btn btn-secondary" style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem' }} disabled={promoting} onClick={promoteToSupplier}>
                    {promoting ? 'Tagging…' : 'Also use as supplier'}
                  </button>
                </span>
              )}
            </div>
          )}
          </>)}

          {/* Lines — EXPENSE: ledger + amount (ADR-0049 §3); ITEM: item × pack × qty (ADR-0058 slice 3) */}
          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <span className="form-label" style={{ margin: 0 }}>Lines</span>
              <button className="btn btn-secondary" onClick={addLine} style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}><IconSystem.add size={12} /> Add line</button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead><tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
                {allowItems && <th style={{ padding: '0.3rem', width: 110 }}>Type</th>}
                <th style={{ padding: '0.3rem', width: 260 }}>{allowItems ? 'Ledger / Item' : 'Ledger'}</th>
                <th style={{ padding: '0.3rem', width: 80 }}>Qty</th><th style={{ padding: '0.3rem', width: 100 }}>Rate</th>
                <th style={{ padding: '0.3rem', width: 130, textAlign: 'right' }}>Amount</th><th style={{ width: 30 }} />
              </tr></thead>
              <tbody>
                {lines.map((l, lineIdx) => {
                  const isItem = allowItems && l.lineType === 'ITEM';
                  const colCount = allowItems ? 6 : 5;
                  const ledgerInfo = ledgerInfoOf(l);
                  // ITEM lines show the resolved ledger as the pre-fill until the user picks another.
                  const shownLedgerId = isItem ? effectiveItemLedgerId(l.expenseLedgerId, ledgerInfo) : l.expenseLedgerId;
                  const ledgerSelect = (placeholder: string) => (
                    <SyutSelect isAsync loadOptions={loadLedgers} defaultOptions cacheOptions={false}
                      seedOptions={shownLedgerId ? [{ value: shownLedgerId, label: ledgerCache.current.get(shownLedgerId) || (shownLedgerId === ledgerInfo?.ledgerId ? prefillLedgerLabel(ledgerInfo) : '') }] : undefined}
                      value={shownLedgerId} onChange={v => updLine(l.key, { expenseLedgerId: (v as string) || '' })} isClearable size="sm" menuPortalTarget={document.body} placeholder={placeholder} />
                  );
                  const computed = isItem ? taxPreview?.byKey[l.key] : undefined;
                  const linePreviewErrors = isItem ? (taxPreview?.lineErrors[l.key] || []) : [];
                  // The charge option shows once the bill has an item line (or while still ticked, so it can be cleared).
                  const showCharge = allowItems && !isItem && (itemRows.length > 0 || l.addToStockCost);
                  const chargeAmount = parseFloat(l.lineAmount) || 0;
                  const allocated = manualAllocated(l);
                  return (
                  <React.Fragment key={l.key}>
                    <tr style={{ borderTop: '1px solid var(--border-color)' }}>
                      {allowItems && (
                        <td style={{ padding: '0.25rem' }}>
                          <SyutSelect options={LINE_TYPE_OPTIONS} value={l.lineType} onChange={v => setLineType(l.key, ((v as string) || 'EXPENSE') as PurchaseLineType)}
                            isClearable={false} size="sm" menuPortalTarget={document.body} />
                        </td>
                      )}
                      <td style={{ padding: '0.25rem' }}>
                        {isItem
                          ? <ItemPickerCell row={l} lookups={itemLookups} onPick={id => { void itemLookups.pickItem(l.key, id, setLines); }} error={errors[`line-${l.key}-item`]} />
                          : ledgerSelect('Expense ledger (or default)')}
                      </td>
                      <td style={{ padding: '0.25rem' }}><input className={`form-input${errors[`line-${l.key}-quantity`] ? ' form-field-error' : ''}`} style={{ fontSize: '0.8125rem' }} type="number" value={l.quantity} title={isItem && l.uomCode ? `In ${l.uomCode}` : undefined} placeholder={isItem ? 'Qty *' : undefined} onChange={e => updLine(l.key, { quantity: e.target.value })} /></td>
                      <td style={{ padding: '0.25rem' }}><input className="form-input" style={{ fontSize: '0.8125rem' }} type="number" value={l.rate} title={isItem && l.uomCode ? `Per ${l.uomCode}` : undefined} onChange={e => updLine(l.key, { rate: e.target.value })} /></td>
                      <td style={{ padding: '0.25rem' }}><input className="form-input" style={{ fontSize: '0.8125rem', textAlign: 'right' }} type="number" value={l.lineAmount} onChange={e => updLine(l.key, { lineAmount: e.target.value })} /></td>
                      <td style={{ textAlign: 'center' }}><button title="Remove" onClick={() => removeLine(l.key)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0.25rem' }}><IconSystem.x size={14} /></button></td>
                    </tr>
                    {isItem && (
                      <tr>
                        <td colSpan={colCount} style={{ padding: '0 0.25rem 0.4rem 0.5rem' }}>
                          <ItemLineDetails row={l} lookups={itemLookups} onChange={p => updLine(l.key, p)}
                            ledger={ledgerSelect('Purchase default')}
                            ledgerInfo={ledgerInfo} pickedLedgerId={l.expenseLedgerId}
                            errors={{ uom: errors[`line-${l.key}-uom`], location: errors[`line-${l.key}-location`] }} />
                        </td>
                      </tr>
                    )}
                    {showCharge && (
                      <tr>
                        <td colSpan={colCount} style={{ padding: '0 0.25rem 0.4rem 0.5rem' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 0.75rem', alignItems: 'center', fontSize: '0.75rem' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', color: 'var(--text-secondary)', cursor: 'pointer' }} title="Freight, handling, clearing… spread onto the bill's item lines">
                              <input type="checkbox" checked={l.addToStockCost} onChange={e => updLine(l.key, { addToStockCost: e.target.checked })} />
                              Add to stock cost
                            </label>
                            {l.addToStockCost && (<>
                              <div style={{ width: 150 }}>
                                <SyutSelect options={ALLOCATION_BASIS_OPTIONS} value={l.allocationBasis} onChange={v => updLine(l.key, { allocationBasis: ((v as string) || 'VALUE') as ChargeAllocationBasis })}
                                  isClearable={false} size="sm" menuPortalTarget={document.body} />
                              </div>
                              <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>The charge debits the items' stock cost, not its own ledger.</span>
                            </>)}
                          </div>
                          {l.addToStockCost && l.allocationBasis === 'MANUAL' && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 0.75rem', alignItems: 'end', marginTop: '0.35rem', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 6, padding: '0.4rem 0.6rem' }}>
                              {itemRows.map(r => (
                                <div key={r.key}>
                                  <label className="form-label" title={r.itemText} style={{ fontSize: '0.68rem', marginBottom: '0.1rem', maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    L{lines.indexOf(r) + 1} · {r.itemText || 'Item'}
                                  </label>
                                  <input className="form-input" style={{ width: 120, textAlign: 'right', fontSize: '0.8rem' }} type="number" placeholder="0.00"
                                    value={l.manualShares[r.key] ?? ''} onChange={e => updLine(l.key, { manualShares: { ...l.manualShares, [r.key]: e.target.value } })} />
                                </div>
                              ))}
                              <span style={{ fontSize: '0.72rem', alignSelf: 'center', color: Math.abs(allocated - chargeAmount) > 0.005 ? '#d97706' : 'var(--text-secondary)' }}>
                                Allocated {money(allocated)} of {money(chargeAmount)}
                              </span>
                            </div>
                          )}
                          {errors[`line-${l.key}-charge`] && <div className="form-error-message">Line {lineIdx + 1}: {errors[`line-${l.key}-charge`]}</div>}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan={colCount} style={{ padding: '0 0.25rem 0.5rem 0.5rem' }}>
                        <textarea className="form-textarea" rows={descFocusKey === l.key ? 3 : 1}
                          style={{ fontSize: '0.8rem', minHeight: descFocusKey === l.key ? '3.6rem' : '2rem', width: '100%', transition: 'min-height 0.12s ease' }}
                          value={l.description} placeholder={isItem ? 'Description… (blank = the item name)' : 'Description…'}
                          onFocus={() => setDescFocusKey(l.key)} onBlur={() => setDescFocusKey(null)}
                          onChange={e => updLine(l.key, { description: e.target.value })} />
                      </td>
                    </tr>
                    {lineTaxCapture && components.length > 0 && (
                      <tr>
                        <td colSpan={colCount} style={{ padding: '0 0.25rem 0.6rem 0.5rem' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 0.75rem', alignItems: 'end', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 6, padding: '0.4rem 0.6rem' }}>
                            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', alignSelf: 'center' }}>
                              Tax on this line{computed ? (l.taxEdited ? ' (edited)' : ' (calculated)') : ''}:
                            </span>
                            {components.map(c => {
                              const v = l.taxes[c.componentCode] ?? { taxAmount: '', eligibleAmount: '' };
                              const rate = computed?.[c.componentCode]?.ratePercentage;
                              return (
                                <React.Fragment key={c.componentCode}>
                                  <div>
                                    <label className="form-label" style={{ fontSize: '0.68rem', marginBottom: '0.1rem' }} title={computed?.[c.componentCode] ? `Computed ${money(computed[c.componentCode].taxAmount)} on ${money(computed[c.componentCode].taxableBase)}` : undefined}>
                                      {c.componentCode}{rate != null ? ` ${rateText(rate)}` : ''}
                                    </label>
                                    <input className="form-input" style={{ width: 110, textAlign: 'right', fontSize: '0.8rem', opacity: componentDisabled(c.componentCode) ? 0.5 : 1 }} type="number" placeholder="0.00"
                                      disabled={!!componentDisabled(c.componentCode)} title={componentDisabled(c.componentCode) || undefined}
                                      value={v.taxAmount} onChange={e => updLineTax(l.key, c.componentCode, { taxAmount: e.target.value })} />
                                  </div>
                                  {itcEligibility === 'PARTIAL' && parseFloat(v.taxAmount) > 0 && (
                                    <div>
                                      <label className="form-label" style={{ fontSize: '0.68rem', marginBottom: '0.1rem' }}>eligible</label>
                                      <input className="form-input" style={{ width: 110, textAlign: 'right', fontSize: '0.8rem' }} type="number" placeholder="0.00"
                                        value={v.eligibleAmount} onChange={e => updLineTax(l.key, c.componentCode, { eligibleAmount: e.target.value })} />
                                    </div>
                                  )}
                                </React.Fragment>
                              );
                            })}
                            {computed && l.taxEdited && (
                              <span style={{ alignSelf: 'center', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.7rem' }}>
                                {differsFromComputed(l.taxes, computed) && <span style={{ color: '#d97706' }}>differs from computed</span>}
                                <button type="button" className="btn btn-secondary" style={{ fontSize: '0.7rem', padding: '0.1rem 0.45rem' }}
                                  title="Use the computed tax again; the line then follows later changes" onClick={() => recalcLine(l.key)}>
                                  Recalculate
                                </button>
                              </span>
                            )}
                          </div>
                          {linePreviewErrors.map((msg, i) => (
                            <div key={i} style={{ fontSize: '0.72rem', color: '#d97706', marginTop: '0.2rem' }}>⚠ {msg}</div>
                          ))}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {errors.lines && <div className="form-error-message">{errors.lines}</div>}
          </div>

          {/* Tax capture (ADR-0049 §4a): the amounts AS PRINTED on the supplier's invoice */}
          {components.length > 0 && (
            <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span className="form-label" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  Tax on the supplier's invoice
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.72rem', fontWeight: 400, color: 'var(--text-secondary)', cursor: 'pointer' }} title="Type the tax against each line instead of one total per component — the line sums show here, and any ineligible tax costs back to its own line's ledger.">
                    <input type="checkbox" checked={lineTaxCapture} onChange={e => { setLineTaxCapture(e.target.checked); setCaptureNotice(null); }} />
                    Capture tax per line
                  </label>
                </span>
                <div style={{ minWidth: 320 }}>
                  <SyutSelect options={ELIGIBILITY_OPTIONS} value={itcEligibility} onChange={v => { eligibilityManual.current = true; setItcEligibility(((v as string) || 'ELIGIBLE') as ItcEligibility); }} isClearable={false} size="sm" menuPortalTarget={document.body} />
                </div>
              </div>
              {captureNotice && lineTaxCapture && (
                <div style={{ fontSize: '0.75rem', color: '#d97706', marginBottom: '0.4rem' }}>⚠ {captureNotice}</div>
              )}
              {previewReason && (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>
                  ⓘ {previewReason}{itcEligibility === 'INELIGIBLE' ? ' Eligibility is Ineligible.' : ' Consider marking the tax Ineligible.'}
                </div>
              )}
              {taxPreview && (taxPreview.errors.length > 0 || Object.keys(taxPreview.lineErrors).length > 0) && (
                <div style={{ fontSize: '0.75rem', color: '#d97706', marginBottom: '0.4rem' }}>
                  <div>⚠ The tax for the item lines could not be fully calculated — enter it as printed:</div>
                  {[...taxPreview.errors, ...Object.values(taxPreview.lineErrors).flat()].map((msg, i) => <div key={i} style={{ paddingLeft: '1rem' }}>{msg}</div>)}
                </div>
              )}
              {lineTaxCapture ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem 1.5rem', alignItems: 'center', fontSize: '0.85rem' }}>
                  {components.map(c => {
                    const sum = lineTaxByComponent[c.componentCode];
                    return (
                      <span key={c.componentCode} style={{ color: 'var(--text-secondary)' }}>
                        {c.componentCode}: <strong>{money(sum?.tax || 0)}</strong>
                        {itcEligibility === 'PARTIAL' && sum && <span style={{ color: 'var(--text-muted)' }}> (eligible {money(sum.eligible)})</span>}
                      </span>
                    );
                  })}
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Summed from the per-line entries below each line; ineligible tax costs back to its own line's ledger.</span>
                </div>
              ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                {taxRows.map(t => (
                  <div key={t.componentCode} style={{ display: 'flex', gap: '0.5rem', alignItems: 'end' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.72rem' }}>{t.componentCode}{t.componentName && t.componentName !== t.componentCode ? ` — ${t.componentName}` : ''}</label>
                      <input className="form-input" style={{ width: 130, textAlign: 'right', opacity: componentDisabled(t.componentCode) ? 0.5 : 1 }} type="number" placeholder="0.00"
                        disabled={!!componentDisabled(t.componentCode)} title={componentDisabled(t.componentCode) || undefined}
                        value={t.taxAmount} onChange={e => updTax(t.componentCode, { taxAmount: e.target.value })} />
                    </div>
                    {itcEligibility === 'PARTIAL' && parseFloat(t.taxAmount) > 0 && (
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label" style={{ fontSize: '0.72rem' }}>of which eligible</label>
                        <input className="form-input" style={{ width: 130, textAlign: 'right' }} type="number" placeholder="0.00"
                          value={t.eligibleAmount} onChange={e => updTax(t.componentCode, { eligibleAmount: e.target.value })} />
                      </div>
                    )}
                  </div>
                ))}
                {itcEligibility !== 'ELIGIBLE' && taxTotal > 0 && (
                  <div className="form-group" style={{ margin: 0, minWidth: 240 }}>
                    <label className="form-label" style={{ fontSize: '0.72rem' }}>Ineligible tax posts to (cost)</label>
                    <SyutSelect isAsync loadOptions={loadLedgers} defaultOptions cacheOptions={false}
                      seedOptions={costLedgerId ? [{ value: costLedgerId, label: ledgerCache.current.get(costLedgerId) || '' }] : undefined}
                      value={costLedgerId} onChange={v => setCostLedgerId((v as string) || '')} isClearable size="sm" menuPortalTarget={document.body} placeholder="Defaults to the first line's ledger" />
                  </div>
                )}
              </div>
              )}
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
                Enter the tax exactly as printed on the supplier's invoice. Eligibility can be revised after posting (Revise ITC) — e.g. by the tax consultant before the return.
              </div>
              {mixedSetNames && (
                <div className="text-danger" style={{ fontSize: '0.75rem', marginTop: '0.3rem' }}>
                  These tax components cannot be mixed on one document: {mixedSetNames}. A supplier's invoice carries one treatment.
                </div>
              )}
              {supplierItcWarning && (
                <div style={{ fontSize: '0.75rem', color: '#d97706', marginTop: '0.3rem' }}>
                  ⚠ {supplierItcWarning}
                </div>
              )}
              {errors.taxes && <div className="form-error-message">{errors.taxes}</div>}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ flex: 1, minWidth: 240 }}>
              <label className="form-label">Narration</label>
              <textarea className="form-textarea" rows={2} value={narration} onChange={e => setNarration(e.target.value)} />
            </div>
            <div style={{ minWidth: 260, fontSize: '0.85rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>Subtotal</span><strong>{money(subtotal)}</strong></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>Input tax (as printed)</span><strong>{money(taxTotal)}</strong></div>
              {taxTotal > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0', color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                  <span>to ITC ledger / to cost</span><span>{money(eligibleTotal)} / {money(toCost)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderTop: '1px solid var(--border-color)', fontSize: '1rem' }}><span>Payable to supplier</span><strong>{currencyCode} {money(grandTotal)}</strong></div>
              {currencyCode && currencyCode !== functionalCurrency && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                  <span>≈ {functionalCurrency} @ {exchangeRate || '—'}</span>
                  <span>{functionalCurrency} {money(grandTotal * (parseFloat(exchangeRate) || 0))}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || (!!editId && !isDirty)} title={editId && !isDirty ? 'No changes to save' : undefined}><IconSystem.check size={16} /> {editId ? 'Save Changes' : isCopy ? 'Post New Bill' : `Post ${docLabel}`}</button>
        </div>
      </div>
      <ConfirmDialog open={confirmOpen}
        title={editId ? 'Save Changes' : `Post ${docLabel}`}
        message={`${editId
          ? `Re-post this ${docLabel.toLowerCase()} for ${currencyCode} ${money(grandTotal)}? The ledger entries will be updated.`
          : isDebitNote
          ? `Post this supplier debit note for ${currencyCode} ${money(grandTotal)}? It adds to what we owe the supplier${againstInvoiceId ? ` on bill ${(againstLabelCache.current.get(againstInvoiceId) || '').split(' ')[0]}` : ''}, as its own payable.`
          : `Post this bill for ${currencyCode} ${money(grandTotal)}? It will be committed to the ledger and create the supplier's payable.`}${duplicates.length > 0 ? ` Note: the supplier number ${supplierInvoiceNumber.trim()} is already recorded on ${duplicates[0].invoiceNumber}.` : ''}`}
        confirmLabel={editId ? 'Save' : 'Post'} cancelLabel="Cancel" variant="default" onConfirm={handleConfirmSave} onCancel={() => setConfirmOpen(false)} />
      <ConfirmDialog open={mismatchMessage != null}
        title="Tax differs from the computed tax"
        message={mismatchMessage || ''}
        confirmLabel="Save anyway" cancelLabel="Go back" variant="default"
        onConfirm={() => { setMismatchMessage(null); setConfirmOpen(true); }}
        onCancel={() => setMismatchMessage(null)} />
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard changes?"
        message="You have unsaved changes on this bill. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

// ---- Edit details modal (ADR-0049 §7, Slice 2c) ----
// The non-financial fields — narration, due date, supplier invoice number, line descriptions.
// No re-post, so it works on settled, locked or ITC-revised bills too.
interface BillDetailsModalProps {
  invoice: PurchaseInvoiceDto | null;
  /** Why the full edit is not available (shown as context), when that is why we are here. */
  fullEditBlocked?: string | null;
  onDone: () => void;
  onClose: () => void;
}
const BillDetailsModal: React.FC<BillDetailsModalProps> = ({ invoice, fullEditBlocked, onDone, onClose }) => {
  const [narration, setNarration] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [supplierNo, setSupplierNo] = useState('');
  const [descriptions, setDescriptions] = useState<Record<number, string>>({});
  const [duplicates, setDuplicates] = useState<SupplierDuplicateProbe[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!invoice) return;
    setNarration(invoice.narration || '');
    setDueDate((invoice.dueDate || '').slice(0, 10));
    setSupplierNo(invoice.supplierInvoiceNumber || '');
    setDescriptions(Object.fromEntries((invoice.lines || []).map(l => [l.lineNo, l.description || ''])));
    setDuplicates([]);
  }, [invoice]);

  if (!invoice) return null;
  const checkDuplicates = async () => {
    const num = supplierNo.trim();
    if (!num || num === (invoice.supplierInvoiceNumber || '')) { setDuplicates([]); return; }
    try { setDuplicates(await purchaseInvoiceService.duplicateCheck(invoice.partyId, num, invoice.id)); } catch { setDuplicates([]); }
  };
  const submit = async () => {
    if (dueDate && dueDate < (invoice.invoiceDate || '').slice(0, 10)) {
      notificationService.toast.warning("The due date cannot be before the supplier's invoice date."); return;
    }
    setSaving(true);
    try {
      await purchaseInvoiceService.updateDetails(invoice.id, {
        rowVersion: invoice.rowVersion,
        narration: narration.trim() || null,
        dueDate: dueDate || null,
        supplierInvoiceNumber: supplierNo.trim() || null,
        lines: (invoice.lines || [])
          .filter(l => (descriptions[l.lineNo] ?? '') !== (l.description || ''))
          .map(l => ({ lineNo: l.lineNo, description: descriptions[l.lineNo]?.trim() || null })),
      });
      notificationService.toast.success(`Bill ${invoice.invoiceNumber || ''} details updated.`);
      onDone(); onClose();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not update the bill details.');
    } finally { setSaving(false); }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        <div className="modal-header" style={{ minHeight: 'auto' }}><h2 className="modal-title">Edit details — {invoice.invoiceNumber}</h2><button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={20} /></button></div>
        <div className="modal-content">
          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
            {fullEditBlocked
              ? <>Amounts, ledgers and tax can't change — <strong>{fullEditBlocked}</strong>. These details can, with no re-posting.</>
              : <>These details change without re-posting the bill.</>}
          </div>
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Supplier Invoice No.</label>
              <input className="form-input" value={supplierNo} maxLength={50} onChange={e => setSupplierNo(e.target.value)} onBlur={checkDuplicates} />
              {duplicates.length > 0 && <div style={{ fontSize: '0.72rem', color: '#d97706', marginTop: '0.2rem' }}>⚠ Already recorded for this supplier: {duplicates.slice(0, 3).map(d => d.invoiceNumber).join(', ')}. Saving is still allowed.</div>}
            </div>
            <div className="form-group"><label className="form-label">Due Date</label><input type="date" className="form-input" value={dueDate} onChange={e => setDueDate(e.target.value)} /></div>
          </div>
          {(invoice.lines || []).length > 0 && (
            <div className="form-group">
              <label className="form-label">Line descriptions</label>
              {(invoice.lines || []).map(l => (
                <div key={l.lineNo} style={{ display: 'flex', gap: '0.5rem', alignItems: 'start', marginBottom: '0.35rem' }}>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', minWidth: 160, paddingTop: '0.4rem' }}>{l.lineNo}. {l.itemId ? itemLabel(l.itemCode, l.itemName) : (l.expenseLedgerName || '—')} · {money(l.lineAmount)}</span>
                  <textarea className="form-textarea" rows={1} style={{ fontSize: '0.8rem', flex: 1 }} value={descriptions[l.lineNo] ?? ''}
                    onChange={e => setDescriptions(prev => ({ ...prev, [l.lineNo]: e.target.value }))} />
                </div>
              ))}
            </div>
          )}
          <div className="form-group"><label className="form-label">Narration</label><textarea className="form-textarea" rows={2} value={narration} onChange={e => setNarration(e.target.value)} /></div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}><IconSystem.check size={16} /> Save Details</button>
        </div>
      </div>
    </>
  );
};

// ---- ITC revision modal (ADR-0049 §4b) ----
interface ReviseItcModalProps {
  invoice: PurchaseInvoiceDto | null;
  onDone: () => void;
  onClose: () => void;
}
const ReviseItcModal: React.FC<ReviseItcModalProps> = ({ invoice, onDone, onClose }) => {
  // Keyed by the tax ROW id — with line-level capture a component can appear on several lines.
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [adjustmentDate, setAdjustmentDate] = useState(today());
  const [reason, setReason] = useState('');
  const [costLedgerId, setCostLedgerId] = useState('');
  const [saving, setSaving] = useState(false);
  const ledgerCache = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (!invoice) return;
    setAmounts(Object.fromEntries((invoice.taxes || []).map(t => [t.id, String(t.eligibleAmount)])));
    setAdjustmentDate(today()); setReason('');
    const seeded = invoice.taxes?.find(t => t.costLedgerId);
    setCostLedgerId(seeded?.costLedgerId || invoice.lines?.[0]?.expenseLedgerId || '');
    if (seeded?.costLedgerId) ledgerCache.current.set(seeded.costLedgerId, seeded.costLedgerName || '');
    const firstLine = invoice.lines?.[0];
    if (firstLine?.expenseLedgerId) ledgerCache.current.set(firstLine.expenseLedgerId, `${firstLine.expenseLedgerCode || ''} - ${firstLine.expenseLedgerName || ''}`);
  }, [invoice]);

  const loadLedgers = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await coaLedgerService.getAll({ pageSize: 50, searchTerm: input || undefined });
      res.data.forEach(l => ledgerCache.current.set(l.id, `${l.ledgerCode} - ${l.ledgerName}`));
      return res.data.map(l => ({ value: l.id, label: `${l.ledgerCode} - ${l.ledgerName}` }));
    } catch { return []; }
  }, []);

  if (!invoice) return null;
  const rowLabel = (t: { lineNo?: number | null; componentCode: string; lineDescription?: string | null }) =>
    t.lineNo == null ? t.componentCode : `L${t.lineNo} · ${t.componentCode}`;
  const changed = (invoice.taxes || []).filter(t => {
    const v = parseFloat(amounts[t.id] ?? '');
    return !isNaN(v) && v !== t.eligibleAmount;
  });
  const submit = async () => {
    for (const t of invoice.taxes || []) {
      const v = parseFloat(amounts[t.id] ?? '');
      if (isNaN(v) || v < 0 || v > t.taxAmount) {
        notificationService.toast.warning(`${rowLabel(t)}: the eligible amount must be between 0 and ${money(t.taxAmount)}.`);
        return;
      }
    }
    if (changed.length === 0) { notificationService.toast.info('Nothing changed.'); return; }
    setSaving(true);
    try {
      await purchaseInvoiceService.reviseItc(invoice.id, {
        rowVersion: invoice.rowVersion,
        adjustmentDate,
        reason: reason.trim() || undefined,
        items: changed.map(t => ({
          lineNo: t.lineNo ?? undefined,
          componentCode: t.componentCode,
          newEligibleAmount: parseFloat(amounts[t.id]),
          costLedgerId: costLedgerId || undefined,
        })),
      });
      notificationService.toast.success('ITC eligibility revised — reclassification journal posted.');
      onDone(); onClose();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not revise the ITC eligibility.');
    } finally { setSaving(false); }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        <div className="modal-header" style={{ minHeight: 'auto' }}><h2 className="modal-title">Revise ITC — {invoice.invoiceNumber}</h2><button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={20} /></button></div>
        <div className="modal-content">
          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
            State the NEW eligible amount per component. The difference posts as a reclassification journal
            dated below (its own open period — the bill itself is not touched).
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem', marginBottom: '0.75rem' }}>
            <thead><tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
              <th style={{ padding: '0.3rem' }}>Component</th>
              <th style={{ padding: '0.3rem', textAlign: 'right' }}>Tax on bill</th>
              <th style={{ padding: '0.3rem', textAlign: 'right' }}>Currently eligible</th>
              <th style={{ padding: '0.3rem', textAlign: 'right', width: 140 }}>New eligible</th>
            </tr></thead>
            <tbody>
              {(invoice.taxes || []).map(t => (
                <tr key={t.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                  <td style={{ padding: '0.3rem' }} title={t.lineDescription || undefined}>{rowLabel(t)}</td>
                  <td style={{ padding: '0.3rem', textAlign: 'right' }}>{money(t.taxAmount)}</td>
                  <td style={{ padding: '0.3rem', textAlign: 'right' }}>{money(t.eligibleAmount)}</td>
                  <td style={{ padding: '0.3rem' }}>
                    <input className="form-input" style={{ textAlign: 'right' }} type="number"
                      value={amounts[t.id] ?? ''}
                      onChange={e => setAmounts(prev => ({ ...prev, [t.id]: e.target.value }))} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="form-grid-2">
            <div className="form-group"><label className="form-label">Adjustment Date</label><input type="date" className="form-input" value={adjustmentDate} onChange={e => setAdjustmentDate(e.target.value)} /><div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>The period of decision — must be open</div></div>
            <div className="form-group">
              <SyutSelect label="Ineligible tax posts to (cost)" isAsync loadOptions={loadLedgers} defaultOptions cacheOptions={false}
                seedOptions={costLedgerId ? [{ value: costLedgerId, label: ledgerCache.current.get(costLedgerId) || '' }] : undefined}
                value={costLedgerId} onChange={v => setCostLedgerId((v as string) || '')} isClearable menuPortalTarget={document.body} />
            </div>
          </div>
          <div className="form-group"><label className="form-label">Reason</label><input className="form-input" value={reason} maxLength={200} onChange={e => setReason(e.target.value)} placeholder="e.g. Blocked credit — staff welfare" /></div>
          {(invoice.adjustments?.length ?? 0) > 0 && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.2rem' }}>Earlier revisions</div>
              {invoice.adjustments.map(a => (
                <div key={a.id}>{(a.adjustmentDate || '').slice(0, 10)} · {a.lineNo != null ? `L${a.lineNo} · ` : ''}{a.componentCode} {a.adjustmentType === 'REVERSAL' ? '−' : '+'}{money(a.amount)}{a.transactionNo ? ` · ${a.transactionNo}` : ''}{a.reason ? ` · ${a.reason}` : ''}</div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || changed.length === 0}><IconSystem.check size={16} /> Post Revision</button>
        </div>
      </div>
    </>
  );
};

// ---- View modal ----
interface BillViewModalProps {
  invoice: PurchaseInvoiceDto | null;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (invoiceNo: string) => void;
}
export const BillViewModal: React.FC<BillViewModalProps> = ({ invoice, onClose, tabId, moduleName, onRestoreView }) => {
  const isDebitNote = invoice?.documentKind === 'DEBIT_NOTE';
  const { minimize } = useModalMinimize();
  const [supplier, setSupplier] = useState<PartyDto | null>(null);
  useEffect(() => {
    setSupplier(null);
    if (!invoice?.partyId) return;
    let active = true;
    partyService.getById(invoice.partyId).then(p => { if (active) setSupplier(p); }).catch(() => {});
    return () => { active = false; };
  }, [invoice?.partyId, invoice?.id]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !invoice) return;
    const invNo = invoice.invoiceNumber || invoice.id;
    minimize({
      id: `${tabId}-view-modal-${invNo}`,
      title: `View - ${invNo}`,
      moduleName, tabId,
      onRestore: () => onRestoreView?.(invoice.invoiceNumber || ''),
    });
    onClose();
  }, [tabId, moduleName, invoice, minimize, onClose, onRestoreView]);

  if (!invoice) return null;
  const hasItems = invoice.lines.some(l => !!l.itemId);
  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        <div className="modal-header" style={{ minHeight: 'auto' }}><h2 className="modal-title">{isDebitNote ? 'Supplier Debit Note' : 'Bill'} {invoice.invoiceNumber}</h2><div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>{tabId && <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar"><IconSystem.chevronDown size={16} /></button>}<button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button></div></div>
        <div className="modal-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
            <div><div style={{ color: 'var(--text-muted)' }}>Supplier</div><strong>{invoice.partyName}</strong>{invoice.supplierInvoiceNumber ? <div style={{ color: 'var(--text-secondary)' }}>Their no: {invoice.supplierInvoiceNumber}</div> : null}</div>
            <div><div style={{ color: 'var(--text-muted)' }}>Dates</div>Invoice {(invoice.invoiceDate || '').slice(0, 10)}<div style={{ color: 'var(--text-secondary)' }}>Booked {(invoice.bookingDate || '').slice(0, 10)}{invoice.dueDate ? ` · due ${(invoice.dueDate || '').slice(0, 10)}` : ''}</div></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Type</div>{PURCHASE_TYPE_OPTIONS.find(o => o.value === invoice.purchaseType)?.label || invoice.purchaseType}</div>
            <div><div style={{ color: 'var(--text-muted)' }}>Status</div>{statusBadges(invoice)}</div>
          </div>
          {isDebitNote && (
            <div style={{ marginBottom: '0.75rem', fontSize: '0.85rem', display: 'flex', flexWrap: 'wrap', gap: '0.25rem 1.5rem' }}>
              <span><span style={{ color: 'var(--text-muted)' }}>Adds to bill </span><strong>{invoice.againstInvoiceNumber || 'No bill (stand-alone)'}</strong></span>
              {invoice.noteReason && <span><span style={{ color: 'var(--text-muted)' }}>Reason </span>{invoice.noteReason}</span>}
            </div>
          )}
          {supplier && (
            <div style={{ marginBottom: '0.75rem', padding: '0.5rem 0.75rem', borderRadius: 6, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', fontSize: '0.8rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.15rem' }}>Supplier</div>
              <div>{supplier.partyName}{supplier.legalName && supplier.legalName !== supplier.partyName ? ` (${supplier.legalName})` : ''}</div>
              {formatAddress(supplier) && <div style={{ color: 'var(--text-secondary)' }}>{formatAddress(supplier)}</div>}
            </div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead><tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}><th style={{ padding: '0.3rem', width: 30 }}>#</th><th style={{ padding: '0.3rem' }}>{hasItems ? 'Ledger / Item' : 'Ledger'}</th><th style={{ padding: '0.3rem' }}>Description</th><th style={{ padding: '0.3rem', textAlign: 'right', width: 120 }}>Amount</th></tr></thead>
            <tbody>{invoice.lines.map(l => (
              <tr key={l.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                <td style={{ padding: '0.3rem', verticalAlign: 'top' }}>{l.lineNo}</td>
                <td style={{ padding: '0.3rem', verticalAlign: 'top' }}>
                  {l.itemId ? (
                    <>
                      <ItemLineSummary l={l} />
                      {l.expenseLedgerName && !l.stockLocationId && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Ledger: {l.expenseLedgerName}</div>}
                    </>
                  ) : (
                    <>
                      {l.expenseLedgerName || '—'}
                      {l.addToStockCost && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Added to stock cost ({BASIS_LABEL[l.allocationBasis || 'VALUE'] || l.allocationBasis})</div>}
                    </>
                  )}
                </td>
                <td style={{ padding: '0.3rem', whiteSpace: 'pre-wrap' }}>{l.description || '—'}</td>
                <td style={{ padding: '0.3rem', textAlign: 'right' }}>{money(l.lineAmount)}</td>
              </tr>
            ))}</tbody>
          </table>
          {(invoice.taxes?.length ?? 0) > 0 && (
            <div style={{ marginTop: '0.75rem' }}>
              <div className="form-label" style={{ marginBottom: '0.25rem' }}>Tax (as printed{invoice.taxCaptureLevel === 'LINE' ? ', per line' : ''}) · ITC {invoice.itcEligibility}</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                <thead><tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}><th style={{ padding: '0.3rem' }}>Component</th><th style={{ padding: '0.3rem', textAlign: 'right' }}>Tax</th><th style={{ padding: '0.3rem', textAlign: 'right' }}>Eligible (ITC)</th><th style={{ padding: '0.3rem', textAlign: 'right' }}>To cost</th></tr></thead>
                <tbody>{invoice.taxes.map(t => (
                  <tr key={t.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '0.3rem' }} title={t.lineDescription || undefined}>{t.lineNo != null ? `L${t.lineNo} · ` : ''}{t.componentCode}</td>
                    <td style={{ padding: '0.3rem', textAlign: 'right' }}>{money(t.taxAmount)}</td>
                    <td style={{ padding: '0.3rem', textAlign: 'right' }}>{money(t.eligibleAmount)}</td>
                    <td style={{ padding: '0.3rem', textAlign: 'right' }}>{money(t.taxAmount - t.eligibleAmount)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
          {(invoice.adjustments?.length ?? 0) > 0 && (
            <div style={{ marginTop: '0.75rem', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
              <div className="form-label" style={{ marginBottom: '0.25rem' }}>ITC revisions</div>
              {invoice.adjustments.map(a => (
                <div key={a.id}>{(a.adjustmentDate || '').slice(0, 10)} · {a.componentCode} {a.adjustmentType === 'REVERSAL' ? '−' : '+'}{money(a.amount)}{a.transactionNo ? ` · journal ${a.transactionNo}` : ''}{a.reason ? ` · ${a.reason}` : ''} · {a.createdByName || ''}</div>
              ))}
            </div>
          )}
          <div style={{ marginLeft: 'auto', maxWidth: 280, marginTop: '0.75rem', fontSize: '0.85rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>Subtotal</span><strong>{money(invoice.subtotal)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>Input tax</span><strong>{money(invoice.taxTotal)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderTop: '1px solid var(--border-color)', fontSize: '1rem' }}><span>Payable</span><strong>{invoice.currencyCode} {money(invoice.grandTotal)}</strong></div>
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}><button className="btn btn-secondary" onClick={onClose}>Close</button></div>
      </div>
    </>
  );
};

// ---- Screen ----
type BillViewMode = 'list' | 'grid';

const STRUCTURAL_COLUMN_IDS = ['select', 'actions'];

const BILL_COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'select', label: '', required: true },
  { id: 'invoiceNumber', label: 'Our No.' },
  { id: 'supplierInvoiceNumber', label: 'Supplier No.' },
  { id: 'bookingDate', label: 'Booked' },
  { id: 'partyName', label: 'Supplier' },
  { id: 'grandTotal', label: 'Total' },
  { id: 'baseTotal', label: 'Total (base)', defaultVisible: false },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'Actions', required: true },
];

interface PurchaseInvoiceScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const PurchaseInvoiceScreen: React.FC<PurchaseInvoiceScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const can = useCan('purchase-invoice');
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();

  // Supplier debit notes live in the Purchase Debit Notes register (ADR-0053); this list is bills.
  const [queryParams, setQueryParams] = useState<PurchaseInvoiceQueryParams>({ page: 1, pageSize: 25, documentKind: 'INVOICE' });
  const { data, loading, pagination, refetch } = usePurchaseInvoices(queryParams);
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(
      () => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })),
      400,
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [viewMode, setViewMode] = useState<BillViewMode>('list');
  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } =
    useColumnVisibility('purchase-invoice-columns', BILL_COLUMN_DEFS);

  const [filterOpen, setFilterOpen] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [includeCancelled, setIncludeCancelled] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [editInvoice, setEditInvoice] = useState<PurchaseInvoiceDto | null>(null);
  const [viewInvoice, setViewInvoice] = useState<PurchaseInvoiceDto | null>(null);
  // "Record supplier debit note" (ADR-0053) opens the same form in debit-note mode, seeded from the bill.
  const [formKind, setFormKind] = useState<PurchaseDocumentKind>('INVOICE');
  const [addsToBill, setAddsToBill] = useState<PurchaseInvoiceDto | null>(null);
  const [reviseInvoice, setReviseInvoice] = useState<PurchaseInvoiceDto | null>(null);
  const [detailsInvoice, setDetailsInvoice] = useState<PurchaseInvoiceDto | null>(null);
  const [detailsBlockedReason, setDetailsBlockedReason] = useState<string | null>(null);
  const [createInstanceId, setCreateInstanceId] = useState<string>('1');
  const [copyInstanceId, setCopyInstanceId] = useState<string>('1');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleView = useCallback(async (r: PurchaseInvoiceDto) => {
    const viewId = tabId ? `${tabId}-view-modal-${r.invoiceNumber || r.id}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId); modal?.onRestore?.();
      return;
    }
    try {
      setViewInvoice(await purchaseInvoiceService.getById(r.id));
    } catch {
      notificationService.toast.error('Could not load the bill.');
    }
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const setupGate = useSetupGate('PURCHASING');

  const handleAdd = useCallback(async () => {
    if (!(await setupGate.ensureReady())) return;
    if (formOpen) formModalMinimizeRef.current?.();
    let next = 1;
    if (tabId) while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++;
    setCreateInstanceId(String(next));
    setFormKind('INVOICE'); setAddsToBill(null);
    setFormMode('create'); setEditInvoice(null); setFormOpen(true);
  }, [formOpen, tabId, isMinimized, setupGate]);

  const handleRecordSupplierDebitNote = useCallback(async (r: PurchaseInvoiceDto) => {
    if (!(await setupGate.ensureReady())) return;
    if (formOpen) formModalMinimizeRef.current?.();
    try {
      const full = await purchaseInvoiceService.getById(r.id);
      let next = 1;
      if (tabId) while (isMinimized(`${tabId}-form-modal-create-sdn-${next}`)) next++;
      setCreateInstanceId(String(next));
      setFormKind('DEBIT_NOTE'); setAddsToBill(full);
      setFormMode('create'); setEditInvoice(null); setFormOpen(true);
    } catch {
      notificationService.toast.error('Could not load the bill.');
    }
  }, [formOpen, tabId, isMinimized, setupGate]);

  const handleEdit = useCallback(async (r: PurchaseInvoiceDto) => {
    if (!(await setupGate.ensureReady())) return;
    const modalId = tabId ? `${tabId}-form-modal-edit-${r.invoiceNumber || r.id}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    try {
      const full = await purchaseInvoiceService.getById(r.id);
      const blocked = editBlockReason(full);
      if (blocked) {
        // A posted bill whose amounts are frozen still has editable details (§7, Slice 2c).
        if (full.docStatus === 'POSTED') { setDetailsBlockedReason(blocked); setDetailsInvoice(full); }
        else notificationService.toast.warning(`This bill cannot be edited — ${blocked}.`);
        return;
      }
      setEditInvoice(full); setFormMode('edit'); setFormOpen(true);
    } catch {
      notificationService.toast.error('Could not load the bill.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleOpenById = useCallback(async (id: string, mode?: 'edit' | 'view') => {
    try {
      const full = await purchaseInvoiceService.getById(id);
      if (mode === 'view') { setViewInvoice(full); return; }
      const blocked = editBlockReason(full);
      if (blocked) {
        setViewInvoice(full);
        notificationService.toast.info(`Opened read-only — ${blocked}.`);
        return;
      }
      setEditInvoice(full); setFormMode('edit'); setFormOpen(true);
    } catch {
      notificationService.toast.error('Could not open the bill.');
    }
  }, []);

  useNavTarget('purchase-invoice', (t) => handleOpenById(t.recordId, t.mode));

  const handleCopy = useCallback(async (r: PurchaseInvoiceDto) => {
    if (formOpen) formModalMinimizeRef.current?.();
    try {
      const full = await purchaseInvoiceService.getById(r.id);
      let next = 1;
      if (tabId) while (isMinimized(`${tabId}-form-modal-copy-${next}`)) next++;
      setCopyInstanceId(String(next));
      setEditInvoice(full); setFormMode('copy'); setFormOpen(true);
    } catch {
      notificationService.toast.error('Could not load the source bill.');
    }
  }, [formOpen, tabId, isMinimized]);

  const handleReviseItc = useCallback(async (r: PurchaseInvoiceDto) => {
    try {
      const full = await purchaseInvoiceService.getById(r.id);
      if (full.docStatus !== 'POSTED') { notificationService.toast.warning('ITC can only be revised on a posted bill.'); return; }
      if ((full.taxes?.length ?? 0) === 0) { notificationService.toast.info('This bill carries no captured tax.'); return; }
      setReviseInvoice(full);
    } catch {
      notificationService.toast.error('Could not load the bill.');
    }
  }, []);

  const handleEditDetails = useCallback(async (r: PurchaseInvoiceDto) => {
    try {
      const full = await purchaseInvoiceService.getById(r.id);
      setDetailsBlockedReason(editBlockReason(full)); setDetailsInvoice(full);
    } catch {
      notificationService.toast.error('Could not load the bill.');
    }
  }, []);

  const handleRestoreModal = useCallback((mode: 'create' | 'edit' | 'copy', instanceId?: string, invoiceNo?: string, kind?: PurchaseDocumentKind) => {
    setFormKind(kind || 'INVOICE');
    if (mode === 'edit') {
      if (invoiceNo) {
        const rec = data.find(r => r.invoiceNumber === invoiceNo);
        if (rec) setEditInvoice(rec);
      }
      setFormMode('edit');
    } else if (mode === 'copy') {
      if (instanceId) setCopyInstanceId(instanceId);
      if (invoiceNo) {
        const rec = data.find(r => r.invoiceNumber === invoiceNo);
        if (rec) setEditInvoice(rec);
      }
      setFormMode('copy');
    } else {
      if (instanceId) setCreateInstanceId(instanceId);
      setFormMode('create');
    }
    setFormOpen(true);
  }, [data]);

  const handleRestoreView = useCallback((invoiceNo: string) => {
    const rec = data.find(r => r.invoiceNumber === invoiceNo);
    if (rec) setViewInvoice(rec);
  }, [data]);

  const handleDelete = useCallback(async (r: PurchaseInvoiceDto) => {
    const label: string = r.invoiceNumber || r.partyName || 'this bill';
    const ok = await confirmDelete({ resourceLabel: label });
    if (!ok) return;
    try {
      await purchaseInvoiceService.remove(r.id, r.rowVersion);
      notificationService.toast.success(`Bill ${r.invoiceNumber || ''} cancelled.`);
      refetch();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not cancel the bill.');
      if (err?.response?.status === 409) refetch();
    }
  }, [confirmDelete, refetch]);

  const columns: SyutGridColumn<PurchaseInvoiceDto>[] = useMemo(() => [
    {
      id: 'invoiceNumber', header: 'Our No.', accessorKey: 'invoiceNumber', size: 130,
      cell: ({ row }) => (row.original as PurchaseInvoiceDto).invoiceNumber || '—',
    },
    {
      id: 'supplierInvoiceNumber', header: 'Supplier No.', accessorKey: 'supplierInvoiceNumber', size: 130,
      cell: ({ row }) => (row.original as PurchaseInvoiceDto).supplierInvoiceNumber || '—',
    },
    {
      id: 'bookingDate', header: 'Booked', accessorKey: 'bookingDate', size: 105,
      cell: ({ row }) => ((row.original as PurchaseInvoiceDto).bookingDate || '').slice(0, 10),
    },
    { id: 'partyName', header: 'Supplier', accessorKey: 'partyName', size: 230 },
    {
      id: 'grandTotal', header: 'Total', accessorKey: 'grandTotal', size: 140, align: 'right',
      cell: ({ row }) => {
        const r = row.original as PurchaseInvoiceDto;
        return <span>{r.currencyCode} {money(r.grandTotal)}</span>;
      },
    },
    {
      // Home-currency equivalent at the bill's booked rate.
      id: 'baseTotal', header: 'Total (base)', accessorKey: 'grandTotal', size: 130, align: 'right',
      cell: ({ row }) => { const r = row.original as PurchaseInvoiceDto; return money(r.grandTotal * (r.exchangeRate || 1)); },
    },
    {
      id: 'status', header: 'Status', accessorKey: 'docStatus', size: 220,
      cell: ({ row }) => statusBadges(row.original as PurchaseInvoiceDto),
    },
  ], []);

  const rowActions = useCallback((r: PurchaseInvoiceDto): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      { id: 'view', label: 'View', icon: <IconSystem.eye size={18} />, onClick: () => handleView(r) },
    ];
    if (can('CREATE')) {
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(r) });
      if (r.docStatus === 'POSTED') {
        items.push({ id: 'supplier-debit-note', label: 'Record supplier debit note', icon: <IconSystem.add size={18} />, onClick: () => handleRecordSupplierDebitNote(r) });
      }
    }
    if (r.docStatus === 'POSTED' && can('UPDATE')) {
      items.push({ id: 'revise-itc', label: 'Revise ITC', icon: <IconSystem.check size={18} />, onClick: () => handleReviseItc(r) });
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(r) });
      items.push({ id: 'edit-details', label: 'Edit details', icon: <IconSystem.edit size={18} />, onClick: () => handleEditDetails(r) });
    }
    if (r.docStatus === 'POSTED' && can('DELETE')) {
      items.push({ id: 'delete', label: 'Cancel Bill', icon: <IconSystem.trash size={18} />, onClick: () => handleDelete(r), variant: 'danger' });
    }
    return items;
  }, [can, handleView, handleEdit, handleEditDetails, handleDelete, handleCopy, handleReviseItc, handleRecordSupplierDebitNote]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAdd, 'New Bill'), visible: can('CREATE') },
    actionButtonFactory.refresh(refetch),
  ];

  const applyFilters = () => {
    setQueryParams(p => ({
      ...p, fromDate: fromDate || undefined, toDate: toDate || undefined,
      includeCancelled: includeCancelled || undefined, page: 1,
    }));
    setFilterOpen(false);
  };
  const clearFilters = () => {
    setFromDate(''); setToDate(''); setIncludeCancelled(false);
    setQueryParams(p => ({ page: 1, pageSize: p.pageSize }));
  };

  const appliedCount = [fromDate, toDate, includeCancelled ? '1' : ''].filter(Boolean).length;

  return (
    <EnterpriseScreenShell
      title="Purchase Invoices"
      description="Record supplier bills — accounting only (ADR-0049)"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.file size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search number / supplier...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as BillViewMode) }}
      filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount }}
      customToolbarItems={
        <ColumnSelector columns={BILL_COLUMN_DEFS} visibleColumns={visibleColumns}
          onApply={applyColumns} onReset={resetColumns} />
      }
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)}>
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From Date</label>
                <input type="date" className="form-input" value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To Date</label>
                <input type="date" className="form-input" value={toDate}
                  onChange={e => setToDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeCancelled}
                    onChange={e => setIncludeCancelled(e.target.checked)} /> Include cancelled
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={columns} enableRowSelection
            visibleColumns={[...STRUCTURAL_COLUMN_IDS, ...visibleColumns.filter(id => !STRUCTURAL_COLUMN_IDS.includes(id))]}
            frozenLeftColumns={['select', 'invoiceNumber']} frozenRightColumns={['actions']}
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={(i) => setQueryParams(p => ({ ...p, page: i + 1 }))}
            onPageSizeChange={(s) => setQueryParams(p => ({ ...p, pageSize: s, page: 1 }))}
            rowActions={rowActions}
            emptyMessage={loading ? 'Loading...' : 'No bills yet — click New Bill.'} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
            }}>
              {data.map(r => (
                <div key={r.id} style={{
                  background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                  borderRadius: '0.75rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                        {r.invoiceNumber || '—'}{r.supplierInvoiceNumber ? ` · ${r.supplierInvoiceNumber}` : ''} · {(r.bookingDate || '').slice(0, 10)}
                      </div>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0.2rem 0 0' }}>{r.partyName}</h3>
                    </div>
                    <ActionMenu items={rowActions(r)} />
                  </div>
                  <div style={{
                    fontSize: '0.78rem', color: 'var(--text-muted)', display: 'flex',
                    justifyContent: 'space-between', gap: '0.5rem', paddingTop: '0.4rem',
                    borderTop: '1px solid var(--border-color)', alignItems: 'center',
                  }}>
                    {statusBadges(r)}
                    <strong>{r.currencyCode} {money(r.grandTotal)}</strong>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <SetupGateModal gate={setupGate} />
      <BillFormModal isOpen={formOpen} mode={formMode} invoice={editInvoice}
        documentKind={formKind} addsTo={addsToBill}
        onSaved={refetch} onClose={() => { setFormOpen(false); setEditInvoice(null); setFormMode('create'); setFormKind('INVOICE'); setAddsToBill(null); }}
        tabId={tabId} moduleName={moduleName || 'Purchase Invoices'}
        createInstanceId={createInstanceId}
        copyInstanceId={copyInstanceId}
        onRestoreModal={handleRestoreModal}
        minimizeRef={formModalMinimizeRef} />

      <ReviseItcModal invoice={reviseInvoice} onDone={refetch} onClose={() => setReviseInvoice(null)} />

      <BillDetailsModal invoice={detailsInvoice} fullEditBlocked={detailsBlockedReason} onDone={refetch}
        onClose={() => { setDetailsInvoice(null); setDetailsBlockedReason(null); }} />

      <BillViewModal invoice={viewInvoice} onClose={() => setViewInvoice(null)}
        tabId={tabId} moduleName={moduleName || 'Purchase Invoices'}
        onRestoreView={handleRestoreView} />
    </EnterpriseScreenShell>
  );
};

export default PurchaseInvoiceScreen;
